import React, { useState, useEffect, useRef, useCallback } from 'react';
import { logger } from '../utils/logger';
import {
  ChevronRight,
  Download,
  Search,
  FolderOpen,
  X,
  RefreshCw,
  Upload,
  FolderPlus,
  GripVertical,
  Check,
  ShieldCheck,
  AlertCircle,
  Edit2,
  Layers,
  LayoutGrid,
  Unlink,
  Users
} from 'lucide-react';

// Google Material Icons for device types
import AcUnitIcon from '@mui/icons-material/AcUnit';
import AirIcon from '@mui/icons-material/Air';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import SensorsIcon from '@mui/icons-material/Sensors';
import LightModeIcon from '@mui/icons-material/LightMode';
import RouterIcon from '@mui/icons-material/Router';
import BlindsIcon from '@mui/icons-material/Blinds';
import PowerIcon from '@mui/icons-material/Power';
import LockIcon from '@mui/icons-material/Lock';
import VideocamIcon from '@mui/icons-material/Videocam';
import SpeakerIcon from '@mui/icons-material/Speaker';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import LayersIcon from '@mui/icons-material/Layers';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  useSortable,
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ultronTuringApi } from '../services/ultronTuringApi';
import {
  getImportedDevices,
  saveImportedDevices,
  getVirtualZones,
  saveVirtualZones,
  cleanupOrphanedDeviceReferences,
  isDeviceUsedAnywhereElse,
  getAllMultiChannelDisplayModes,
  setMultiChannelDisplayMode,
  getDeviceControlGroups,
  deleteDeviceControlGroup,
  type Device,
  type DeviceZone,
  type DeviceControlGroup
} from '../services/userPreferencesApi';
import { syncWebhookDevices } from '../services/deviceSubscriptionManager';
import {
  runHealthCheck,
  getQuickStatus,
  type HealthCheckResult
} from '../services/subscriptionHealthCheck';
// Note: Global battery alert (alertType: 'battery') dynamically queries ALL devices
// Server handles everything - no need to sync on import
import { getBatteryAlert } from '../services/userPreferencesApi';

interface DeviceManagerSidebarProps {
  onDeviceSelectionChange?: (selectedDevices: string[]) => void;
  onDevicesImported?: () => void;  // Called after devices are imported to refresh Dashboard
  currentBoard: 'intelliSpace';  // Legacy boards removed - only intelliSpace remains
  onExpandedChange?: (expanded: boolean) => void;
  appSidebarExpanded?: boolean;
  onZonesChange?: (zones: DeviceZone[]) => void;  // Called when zone order changes for real-time sync
}

// Props interface for DroppableGroup
interface DroppableGroupProps {
  group: DeviceZone;
  isExpanded: boolean;
  isRenaming: boolean;
  tempGroupName: string;
  isSelected: boolean;
  deviceShadowMap: Record<string, any>;
  controlGroups: DeviceControlGroup[];  // Control groups in this zone
  onToggleExpanded: () => void;
  onSelect: () => void;
  onStartRename: () => void;
  onRename: (newName: string) => void;
  onCancelRename: () => void;
  onTempNameChange: (value: string) => void;
  onDelete: () => void;
  onRemoveDevice: (deviceSN: string) => void;
  onReorderDevices: (zoneId: string, newDeviceOrder: string[]) => void;
  onUngroupControlGroup: (groupId: string) => void;  // Ungroup a control group
}

// Sortable Device Item component for drag-and-drop reordering within a zone
interface SortableDeviceItemProps {
  deviceSN: string;
  displayName: string;
  onRemove: (deviceSN: string) => void;
}

const SortableDeviceItem = React.memo(({ deviceSN, displayName, onRemove }: SortableDeviceItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: deviceSN });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`flex items-center justify-between text-sm py-2 px-3 rounded-lg border transition-all ${
        isDragging 
          ? 'bg-slate-700/70 border-blue-500/50 shadow-lg' 
          : 'bg-slate-800/30 border-transparent hover:bg-slate-700/40 hover:border-slate-600/30'
      }`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-slate-600/50 rounded"
        >
          <GripVertical className="w-3.5 h-3.5 text-slate-500 hover:text-slate-300" />
        </div>
        <span className="text-slate-300 truncate">{displayName}</span>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(deviceSN);
        }}
        className="p-1 hover:bg-red-900/30 rounded opacity-60 hover:opacity-100 transition-opacity"
        title="Remove from zone"
      >
        <X className="w-3.5 h-3.5 text-red-400" />
      </button>
    </div>
  );
});

// Extracted DroppableGroup component - defined outside to prevent recreation on parent re-render
const DroppableGroupComponent = React.memo(({
  group,
  isExpanded,
  isRenaming,
  tempGroupName,
  isSelected,
  deviceShadowMap,
  controlGroups,
  onToggleExpanded,
  onSelect,
  onStartRename,
  onRename,
  onCancelRename,
  onTempNameChange,
  onDelete,
  onRemoveDevice,
  onReorderDevices,
  onUngroupControlGroup,
}: DroppableGroupProps) => {
  // Sensors for device reordering within zone
  const reorderSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement required to start drag
      },
    })
  );

  // Handle device reorder within this zone
  const handleDeviceDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = group.devices.indexOf(active.id as string);
    const newIndex = group.devices.indexOf(over.id as string);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(group.devices, oldIndex, newIndex);
      onReorderDevices(group.id, newOrder);
    }
  }, [group.devices, group.id, onReorderDevices]);
  const {
    setNodeRef,
    isOver,
  } = useDroppable({ id: group.id });

  return (
    <div
      ref={setNodeRef}
      className={`group/group p-3 border rounded-lg transition-all ${
        isOver
          ? 'border-blue-500 bg-blue-900/20 border-2'
          : 'border-slate-500/50 hover:border-slate-400/60 hover:bg-slate-700/30'
      }`}
    >
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={onToggleExpanded}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Checkbox for group selection */}
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            onClick={(e) => e.stopPropagation()}
            className="mr-2 flex-shrink-0"
          />
          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
          <FolderOpen className="w-4 h-4 text-slate-400 flex-shrink-0" />
          {isRenaming ? (
            <input
              type="text"
              value={tempGroupName}
              onChange={(e) => onTempNameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing && e.keyCode !== 229) {
                  e.preventDefault();
                  onRename(tempGroupName);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancelRename();
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 min-w-0 px-2 py-0.5 text-sm font-medium border border-blue-500 rounded bg-[#0C2340] text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          ) : (
            <span className="font-medium text-sm text-white truncate">
              {group.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-xs text-slate-400">
            {group.devices.length} devices
          </span>
          {/* Rename button - show on hover, hide when editing */}
          {!isRenaming && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStartRename();
              }}
              className="opacity-0 group-hover/group:opacity-100 p-1 hover:bg-blue-100 hover:bg-blue-900/30 rounded transition-opacity"
              title="Rename zone"
            >
              <Edit2 className="w-4 h-4 text-blue-500" />
            </button>
          )}
          {/* Delete button - show on hover */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="opacity-0 group-hover/group:opacity-100 p-1 hover:bg-red-900/30 rounded transition-opacity"
            title="Delete zone"
          >
            <X className="w-4 h-4 text-red-500" />
          </button>
        </div>
      </div>

      {/* Expanded Zone Content */}
      {isExpanded && (
        <div className="mt-2 space-y-2">
          {/* Device Control Groups (Apple Home style) */}
          {controlGroups.length > 0 && (
            <div className="space-y-1.5">
              {controlGroups.map(cg => (
                <div
                  key={cg.id}
                  className="flex items-center justify-between py-2 px-3 bg-blue-900/20 border border-blue-600/30 rounded-lg hover:bg-blue-900/30 transition-colors"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-blue-600/30 rounded">
                      <GripVertical className="w-3.5 h-3.5 text-blue-400/60 hover:text-blue-300" />
                    </div>
                    <LayersIcon className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-medium text-blue-300 truncate">{cg.name}</span>
                    <span className="text-xs text-blue-400/60">({cg.devices.length})</span>
                  </div>
                  <button
                    onClick={() => onUngroupControlGroup(cg.id)}
                    className="p-1 hover:bg-red-900/30 rounded opacity-60 hover:opacity-100 transition-opacity"
                    title="解散群組"
                  >
                    <X className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Individual Devices - with drag-and-drop reordering */}
          {group.devices.length > 0 && (
            <DndContext
              sensors={reorderSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDeviceDragEnd}
            >
              <SortableContext
                items={group.devices}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-1.5">
                  {group.devices.map(deviceSN => {
                    // Skip devices that are in a control group
                    const isInControlGroup = controlGroups.some(cg => cg.devices.includes(deviceSN));
                    if (isInControlGroup) return null;
                    
                    let device = deviceShadowMap[deviceSN];
                    if (!device) {
                      const baseSN = deviceSN.split('#')[0];
                      device = deviceShadowMap[baseSN];
                    }
                    const displayName = device 
                      ? (device.nickname || device.displayName || device.name || device.deviceName || deviceSN)
                      : deviceSN;
                    
                    return (
                      <SortableDeviceItem
                        key={deviceSN}
                        deviceSN={deviceSN}
                        displayName={displayName}
                        onRemove={onRemoveDevice}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
    </div>
  );
});

export default function DeviceManagerSidebar({
  onDeviceSelectionChange,
  onDevicesImported,
  currentBoard,
  onExpandedChange,
  appSidebarExpanded = true,
  onZonesChange
}: DeviceManagerSidebarProps) {
  // Debug log
  logger.log('🔧 DeviceManagerSidebar appSidebarExpanded:', appSidebarExpanded);

  // Device Tab States
  const [availableDevices, setAvailableDevices] = useState<Device[]>([]);
  const [importedDevices, setImportedDevices] = useState<Device[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>('all');  // Device type filter
  const [loading, setLoading] = useState(false);
  const [availableGroups, setAvailableGroups] = useState<any[]>([]);
  const [selectedSourceGroup, setSelectedSourceGroup] = useState<string>('');
  
  // Multi-channel display mode preferences (baseSN -> 'merged' | 'expanded')
  const [multiChannelDisplayModes, setMultiChannelDisplayModes] = useState<Record<string, 'merged' | 'expanded'>>({});
  // Track which multi-channel devices have their channels expanded in the UI
  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set());

  // Group Tab States
  const [deviceZones, setDeviceZones] = useState<DeviceZone[]>([]);
  
  // Device Control Groups (Apple Home style)
  const [deviceControlGroups, setDeviceControlGroups] = useState<DeviceControlGroup[]>([]);

  // Listen for zones order changes from Control Page for real-time sync
  useEffect(() => {
    const handleZonesOrderChanged = (event: CustomEvent<{ zones: DeviceZone[] }>) => {
      setDeviceZones(event.detail.zones);
    };
    
    window.addEventListener('zones-order-changed', handleZonesOrderChanged as EventListener);
    return () => {
      window.removeEventListener('zones-order-changed', handleZonesOrderChanged as EventListener);
    };
  }, []);

  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [renamingGroupId, setRenamingGroupId] = useState<string | null>(null);
  const [tempGroupName, setTempGroupName] = useState('');

  // Drag and Drop States
  const [activeDevice, setActiveDevice] = useState<Device | null>(null);

  // Selection for adding to groups
  const [selectedForGroup, setSelectedForGroup] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]); // New: For selecting target groups

  // Track expanded groups
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Sync device states
  const [syncing, setSyncing] = useState(false);
  const [syncNotification, setSyncNotification] = useState<{ message: string; type: 'success' | 'info' | 'error' } | null>(null);

  // Health Check states
  const [healthCheckResult, setHealthCheckResult] = useState<HealthCheckResult | null>(null);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const [quickStatus, setQuickStatus] = useState<{
    totalImported: number;
    webhookCount: number;
    websocketCount: number;
    lastCheck: Date;
  } | null>(null);

  // Ref to maintain scroll position
  const importedDevicesScrollRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  // Ref to track if auto-cleanup has been performed
  const hasPerformedAutoCleanup = useRef(false);

  // ✅ 規劃原則：device shadow 是單一資料來源，存放所有已匯入設備的完整資訊（含 nickname）
  const [deviceShadowMap, setDeviceShadowMap] = useState<Record<string, any>>({});

  // Maintain scroll position when selectedForGroup changes
  useEffect(() => {
    const container = importedDevicesScrollRef.current;
    if (container) {
      // Restore scroll position after render
      requestAnimationFrame(() => {
        container.scrollTop = scrollPositionRef.current;
      });
    }
  }, [selectedForGroup]);

  // Save scroll position before state update
  const handleDeviceSelection = useCallback((deviceSN: string, checked: boolean) => {
    const container = importedDevicesScrollRef.current;
    if (container) {
      scrollPositionRef.current = container.scrollTop;
    }

    setSelectedForGroup(prev => {
      if (checked) {
        return [...prev, deviceSN];
      } else {
        return prev.filter(sn => sn !== deviceSN);
      }
    });
  }, []);

  // Sidebar expand/collapse state

  // Setup drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    })
  );

  // Load initial data
  useEffect(() => {
    loadAvailableGroups();
    loadImportedDevices();
    loadDeviceZones();
    loadMultiChannelDisplayModes();
  }, []);
  
  // Load multi-channel display mode preferences from Firestore
  const loadMultiChannelDisplayModes = async () => {
    try {
      const modes = await getAllMultiChannelDisplayModes();
      setMultiChannelDisplayModes(modes);
      logger.log('📐 Loaded multi-channel display modes:', Object.keys(modes).length);
    } catch (error) {
      logger.error('Failed to load multi-channel display modes:', error);
    }
  };

  // ✅ 規劃原則：載入所有 importedDevices 和 deviceZones 中設備的 device shadow（從 QueryDevices API 獲取 nickname 等完整資訊）
  // 優化：使用 localStorage 快取 + 並行載入（不等待 importedDevices）
  useEffect(() => {
    const loadDeviceShadows = async () => {
      try {
        // 1. 先從 localStorage 快取載入（立即顯示，無需等待 API）
        const cacheKey = `device-shadow-cache-${currentBoard}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          try {
            const cachedMap = JSON.parse(cached);
            const cacheTimestamp = cachedMap._timestamp || 0;
            const cacheAge = Date.now() - cacheTimestamp;
            // 快取有效期：5 分鐘
            if (cacheAge < 5 * 60 * 1000 && Object.keys(cachedMap).length > 0) {
              delete cachedMap._timestamp; // 移除時間戳
              setDeviceShadowMap(cachedMap);
              logger.log(`⚡ [DeviceShadow] Loaded ${Object.keys(cachedMap).length} device shadows from cache`);
            }
          } catch (e) {
            logger.warn(`⚠️ [DeviceShadow] Failed to parse cache:`, e);
          }
        }

        // 2. 收集所有需要查詢的 deviceSN：
        // 優先從 deviceZones（通常先載入完成）
        const groupSNs = deviceZones
          .flatMap(group => group.devices || [])
          .filter((sn): sn is string => !!sn);

        // 從 importedDevices（可能還在載入中）
        const importedSNs = importedDevices
          .map(d => (d as any).deviceSN || d.sn || (d as any).deviceId)
          .filter((sn): sn is string => !!sn);

        // 合併並去重
        const allSNs = Array.from(new Set([...importedSNs, ...groupSNs]));

        if (allSNs.length === 0) {
          setDeviceShadowMap({});
          return;
        }

        // 3. 檢查快取中已有的設備
        const cachedMap = cached ? (() => {
          try {
            const parsed = JSON.parse(cached);
            const cacheTimestamp = parsed._timestamp || 0;
            delete parsed._timestamp;
            // 如果快取超過 1 分鐘，標記需要完整刷新 nickname
            const cacheAge = Date.now() - cacheTimestamp;
            const needsRefresh = cacheAge > 60 * 1000; // 1 分鐘後刷新 nickname
            return { map: parsed, needsRefresh };
          } catch {
            return { map: {}, needsRefresh: true };
          }
        })() : { map: {}, needsRefresh: true };
        
        // 如果需要刷新，查詢所有設備以獲取最新 nickname
        const missingSNs = cachedMap.needsRefresh 
          ? allSNs // 刷新所有設備的 nickname
          : allSNs.filter(sn => !cachedMap.map[sn]); // 只查詢缺失的
        
        if (missingSNs.length === 0 && !cachedMap.needsRefresh) {
          logger.log(`✅ [DeviceShadow] All ${allSNs.length} device shadows already in cache`);
          // ✅ Fix: Set deviceShadowMap from cache when all devices are already cached
          setDeviceShadowMap(cachedMap.map);
          return;
        }
        
        logger.log(`🔄 [DeviceShadow] ${cachedMap.needsRefresh ? 'Refreshing all' : 'Loading missing'} device shadows...`);

        // 調用 QueryDevices API 獲取 device shadow（包含 nickname）
        const result = await ultronTuringApi.queryDevices(undefined, undefined, { sns: missingSNs, detailed: true });

        if (result && Array.isArray(result) && result.length > 0) {
          // 合併快取和新查詢的結果（新的 API 結果會覆蓋舊的快取）
          const map: Record<string, any> = { ...cachedMap.map };
          
          result.forEach((d: any) => {
            const deviceSN = d.deviceSN || d.sn || d.deviceId;
            const baseSN = d.baseSN || (deviceSN ? deviceSN.split('#')[0] : null);
            
            if (deviceSN) {
              // 主要 key：使用完整的 deviceSN（帶 endpoint）
              map[deviceSN] = d;
              
              // 多端點設備：同時用 baseSN 作為 key（如果不同）
              if (baseSN && baseSN !== deviceSN) {
                // 如果 baseSN 還沒有對應的設備，或者當前設備是第一個端點，則使用它
                if (!map[baseSN] || (d.endpoint && d.endpoint === '0')) {
                  map[baseSN] = d;
                }
              }
              
              // 為所有可能的端點格式建立映射（處理 group.devices 中可能存的不同格式）
              if (baseSN && d.endpoint !== undefined && d.endpoint !== null) {
                const endpointSN = `${baseSN}#${d.endpoint}`;
                if (endpointSN !== deviceSN) {
                  map[endpointSN] = d;
                }
              }
            }
          });
          
          // 保存到 localStorage 快取
          try {
            const cacheData = { ...map, _timestamp: Date.now() };
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
          } catch (e) {
            logger.warn(`⚠️ [DeviceShadow] Failed to save cache:`, e);
          }
          
          setDeviceShadowMap(map);
          logger.log(`✅ [DeviceShadow] Loaded ${Object.keys(map).length} device shadows (${result.length} from API, ${Object.keys(cachedMap.map).length} from cache)`);
        } else {
          // 如果 API 失敗，至少使用快取
          if (Object.keys(cachedMap.map).length > 0) {
            setDeviceShadowMap(cachedMap.map);
            logger.log(`⚠️ [DeviceShadow] API failed, using ${Object.keys(cachedMap.map).length} cached device shadows`);
          } else {
            logger.warn(`⚠️ [DeviceShadow] No device shadows returned from API and no cache available`);
            setDeviceShadowMap({});
          }
        }
      } catch (error) {
        logger.error(`❌ [DeviceShadow] Failed to load device shadows:`, error);
        setDeviceShadowMap({});
      }
    };

    loadDeviceShadows();
  }, [importedDevices, deviceZones]);

  // Auto-cleanup on initialization and when devices are loaded
  useEffect(() => {
    // Only run if:
    // 1. availableGroups have been loaded
    // 2. importedDevices exist
    // 3. Haven't performed auto-cleanup yet
    if (availableGroups.length > 0 && importedDevices.length > 0 && !hasPerformedAutoCleanup.current) {
      logger.log('🔄 [Auto-Cleanup] Triggering auto-cleanup on initialization');
      hasPerformedAutoCleanup.current = true;
      // Delay slightly to ensure all data is loaded
      setTimeout(() => {
        syncDevices(true); // silent mode
      }, 500);
    }
  }, [availableGroups, importedDevices]);

  // Listen for device name updates
  useEffect(() => {
    logger.log('🎧 [DeviceManager] Registering device-name-updated listener for board:', currentBoard);

    const handleDeviceNameUpdate = (event: CustomEvent) => {
      const { sn, name } = event.detail;
      logger.log(`📝 [DeviceManager] Device name updated event received: ${sn} -> ${name}`);
      logger.log(`📝 [DeviceManager] Current board: ${currentBoard}`);
      logger.log(`📝 [DeviceManager] Current imported devices:`, importedDevices);

      // Update imported devices - prioritize nickname since that's what ConfigureDevice API saves
      setImportedDevices(prev => {
        logger.log(`📝 [DeviceManager] Previous imported devices:`, prev);
        const updated = prev.map(device => {
          if (device.sn === sn) {
            logger.log(`📝 [DeviceManager] Found matching device, updating...`, device);
            return {
              ...device,
              nickname: name,      // Update nickname (highest priority in display)
              deviceName: name,    // Also update deviceName for consistency
              name: name           // Also update name for consistency
            };
          }
          return device;
        });

        logger.log(`📝 [DeviceManager] Updated imported devices:`, updated);

        // Also save to localStorage (simplified: only intelliSpace board)
        const key = 'imported-devices-intelliSpace';
        localStorage.setItem(key, JSON.stringify(updated));
        logger.log(`📝 [DeviceManager] Saved to localStorage key: ${key}`);

        // ✅ Sync to Firestore for persistence across sessions
        saveImportedDevices('intelliSpace', updated)
          .then(() => {
            logger.log(`✅ [DeviceManager] Nickname synced to Firestore: ${sn} -> ${name}`);
          })
          .catch(err => {
            logger.error(`❌ [DeviceManager] Failed to sync nickname to Firestore:`, err);
          });

        return updated;
      });
    };

    window.addEventListener('device-name-updated', handleDeviceNameUpdate as EventListener);
    logger.log('✅ [DeviceManager] Listener registered');

    return () => {
      logger.log('🔌 [DeviceManager] Removing device-name-updated listener');
      window.removeEventListener('device-name-updated', handleDeviceNameUpdate as EventListener);
    };
  }, [currentBoard, importedDevices]);

  // Load available groups from user account
  const loadAvailableGroups = async () => {
    try {
      const result = await ultronTuringApi.queryLocations();
      logger.log('📍 Loaded locations/groups:', result);

      // Handle different response formats
      let locations: Array<{ id?: string; groupId?: string; name?: string; [key: string]: unknown }> = [];
      if (result.success && result.data) {
        // New format: { success: true, data: [...] }
        if (Array.isArray(result.data)) {
          locations = result.data;
        } else if (typeof result.data === 'object' && result.data !== null) {
          const dataObj = result.data as { locations?: Array<{ id?: string; groupId?: string; name?: string; [key: string]: unknown }>; groups?: Array<{ id?: string; groupId?: string; name?: string; [key: string]: unknown }> };
          locations = dataObj.locations || dataObj.groups || [];
        }
      } else if (Array.isArray(result)) {
        // Old format: direct array
        locations = result;
      }

      if (locations.length > 0) {
        setAvailableGroups(locations);
        logger.log('📍 Available groups set:', locations.map((g: any) => ({ id: g.id || g.groupId, name: g.name })));
        // Set first group as default
        if (!selectedSourceGroup && locations.length > 0) {
          const firstLocation = locations[0];
          const firstId = (firstLocation.id || firstLocation.groupId || '') as string;
          if (firstId) {
            setSelectedSourceGroup(firstId);
          }
        }
      }
    } catch (error) {
      logger.error('Failed to load groups:', error);
    }
  };

  // Load devices from selected group
  const loadDevicesFromGroup = async (groupId: string, currentImportedDevices?: Device[]) => {
    if (!groupId) return;

    setLoading(true);
    try {
      const deviceList = await ultronTuringApi.queryDevices(groupId, undefined, { 
        detailed: true,
        multiChannelDisplayModes: multiChannelDisplayModes
      });
      if (deviceList && Array.isArray(deviceList)) {
        // Use provided imported devices or current state
        const importedList = currentImportedDevices || importedDevices;

        const formattedDevices: Device[] = deviceList
          .filter((device: any) => device && (device.sn || device.deviceSN || device.baseSN))
          .map((device: any) => {
            // ✅ SIMPLIFIED: API now returns correct device structure via endpointRelationship
            // - single/composite: API returns ONE merged device with baseSN
            // - multi_channel/independent_devices: API returns SEPARATE devices with full deviceSN
            const deviceSn = device.sn || device.deviceSN || device.baseSN;
            const baseSn = device.baseSN || deviceSn.split('#')[0];

            if (!deviceSn) {
              logger.warn('⚠️ Skipping device with no identifier:', device);
              return null;
            }

            // Construct display name with fallback chain
            let displayName;
            if (device.nickname && device.nickname.trim()) {
              // Priority 1: User nickname - use as-is
              displayName = device.nickname;
            } else if (device.displayName && device.displayName.trim()) {
              // Priority 2: API displayName (from UltronCloud)
              displayName = device.displayName;
            } else if (device.name && device.name.trim()) {
              // Priority 3: Device name
              displayName = device.name;
            } else if (device.deviceName && device.deviceName.trim()) {
              // Priority 4: Device name (alternate field)
              displayName = device.deviceName;
            } else if (device.endpoint) {
              // Priority 5: No proper name, use baseSN + endpoint for identification
              const baseName = device.baseSN || deviceSn.split('#')[0];
              displayName = `${baseName} #${device.endpoint}`;
            } else {
              // Priority 6: Fallback to device SN
              displayName = deviceSn;
            }


            // Fix: Check both 'id' and 'groupId' fields to find matching group
            const group = availableGroups.find(g => {
              const gId = g.id || g.groupId;
              return gId === groupId;
            });


            // Determine online status - explicitly check for false/true, not just truthy/falsy
            const isOnline = device.online === true || device.online === 'true' || device.online === 1;
            const isOffline = device.online === false || device.online === 'false' || device.online === 0;
            const deviceStatus = isOnline ? 'online' : (isOffline ? 'offline' : 'offline'); // Default to offline if unclear
            
            const deviceData = {
              sn: deviceSn,  // Use device SN (may include #endpoint for multi-channel)
              baseSN: baseSn, // Base SN without endpoint
              nickname: device.nickname,
              displayName: device.displayName || '',  // Preserve original API displayName
              name: device.name || '',                // Preserve original API name
              deviceName: displayName,                // Store computed display name
              type: device.type || 'unknown',
              groupId: groupId,
              groupName: group?.name || group?.groupId || group?.id || 'Unknown Zone',
              status: deviceStatus as 'online' | 'offline',
              // ✅ Check if device is in ANY virtual group (not just importedDevices)
              imported: deviceZones.some(group => group.devices.includes(deviceSn)),
              // ✅ NEW: Pass through capabilityTypes for badge display (merged devices)
              capabilityTypes: device.capabilityTypes || [],
              // ✅ NEW: Pass through endpointRelationship for reference
              endpointRelationship: device.endpointRelationship,
              traits: device.traits || [],
              online: isOnline,
              // ✅ Include attributes for sensor capability detection (lux, temperature, humidity, etc.)
              attributes: device.attributes
            };
            
            return deviceData;
          })
          .filter(Boolean) as Device[];
        
        // ✅ SIMPLIFIED: API now handles merging via endpointRelationship
        // Only deduplicate exact same SNs (rare API duplicates)
        const uniqueDevices = formattedDevices.reduce((acc: Device[], current: Device) => {
          const exactDuplicate = acc.find(d => d.sn === current.sn);
          if (exactDuplicate) {
            logger.log(`⚠️ Skipping exact duplicate: ${current.sn}`);
            return acc;
          }
          acc.push(current);
          return acc;
        }, []);
        
        logger.log(`📋 Filtered ${formattedDevices.length} -> ${uniqueDevices.length} unique devices`);
        
        // ✅ Query device status to get accurate online/offline status
        // queryDevices API may not return online status, so we need to query it separately
        // ✅ IMPORTANT: baseSN's online status represents ALL endpoints (#1, #2, etc.)
        if (uniqueDevices.length > 0) {
          try {
            // ✅ Extract baseSNs (remove endpoint suffix) and deduplicate
            // queryStatus API automatically converts deviceSNs to baseSNs, so we should use baseSNs directly
            const baseSNs = Array.from(new Set(
              uniqueDevices.map(d => {
                const deviceSN = d.sn || (d as any).deviceSN || (d as any).baseSN;
                // Extract baseSN (remove #endpoint suffix)
                return deviceSN ? deviceSN.split('#')[0] : '';
              }).filter((sn): sn is string => typeof sn === 'string' && sn.length > 0)
            ));
            
            if (baseSNs.length > 0) {
              logger.log(`🔍 [DeviceManager] Querying status for ${baseSNs.length} unique baseSNs (representing ${uniqueDevices.length} devices)`);
              
              // ✅ OPTIMIZED: Query ALL devices in a single API call
              // MCP API can handle large device lists efficiently
              const statusMap: Record<string, any> = {};
              
              try {
                const batchResult = await ultronTuringApi.queryStatus(baseSNs, { detailed: false });
                
                if (batchResult && typeof batchResult === 'object') {
                  // ✅ Handle different response formats from QueryStatus API
                  if (batchResult.devices && typeof batchResult.devices === 'object') {
                    // NEW format: { devices: { "baseSN": { online, stateData } } }
                    Object.entries(batchResult.devices).forEach(([deviceId, status]: [string, any]) => {
                      const baseSN = deviceId.split('#')[0];
                      if (status?.stateData) {
                        statusMap[baseSN] = { ...status, ...status.stateData };
                      } else {
                        statusMap[baseSN] = status;
                      }
                    });
                    logger.log(`✅ [DeviceManager] Using new devices object format`);
                  } else if (batchResult.data && Array.isArray(batchResult.data)) {
                    // MCP format: { data: [{ system: { baseSN, online }, states: [{ endpoint, deviceSN, values }] }] }
                    
                    // ✅ Helper to flatten nested values: { lux: { value: [9] } } -> { lux: 9 }
                    const flattenValues = (values: Record<string, any>) => {
                      const flat: Record<string, any> = {};
                      if (!values) return flat;
                      
                      Object.entries(values).forEach(([key, data]) => {
                        if (data && typeof data === 'object' && 'value' in data) {
                          // Handle array values: lux: { value: [9] } -> lux: 9
                          flat[key] = Array.isArray(data.value) ? data.value[0] : data.value;
                        }
                      });
                      return flat;
                    };
                    
                    batchResult.data.forEach((item: any) => {
                      // ✅ FIX: Support nested system structure from QueryStatus API
                      const system = item.system || {};
                      const baseSN = system.baseSN || system.sn || item.deviceSN || item.baseSN || item.device || item.deviceId || item.sn;
                      const states = item.states || [];
                      const online = system.online ?? item.online;
                      
                      if (baseSN) {
                        const cleanBaseSN = baseSN.split('#')[0];
                        
                        // ✅ Store baseSN level status (for devices without endpoints)
                        if (!statusMap[cleanBaseSN]) {
                          statusMap[cleanBaseSN] = { ...system, online, baseSN: cleanBaseSN };
                        } else {
                          // Keep online status as true if any source shows online
                          statusMap[cleanBaseSN].online = statusMap[cleanBaseSN].online || online;
                        }
                        
                        // ✅ FIX: Process each endpoint's state and store by deviceSN
                        states.forEach((state: any) => {
                          // ✅ FIX: Validate that we have a valid device identifier
                          // Priority: state.deviceSN > baseSN#endpoint > baseSN (fallback for single-endpoint devices)
                          let deviceSN: string;
                          if (state.deviceSN) {
                            deviceSN = state.deviceSN;
                          } else if (state.endpoint !== undefined && state.endpoint !== null) {
                            deviceSN = `${cleanBaseSN}#${state.endpoint}`;
                          } else {
                            // Fallback to cleanBaseSN for devices without explicit endpoint
                            deviceSN = cleanBaseSN;
                          }
                          
                          const values = state.values || {};
                          const flattenedValues = flattenValues(values);
                          
                          statusMap[deviceSN] = {
                            ...system,
                            online,
                            baseSN: cleanBaseSN,
                            endpoint: state.endpoint,
                            values: values,
                            // Spread flattened values for direct access (e.g., device.lux, device.occupancy)
                            ...flattenedValues
                          };
                          
                          // ✅ FIX: Also update baseSN entry with flattened values
                          // This is needed for merged devices (single/composite) that use baseSN as their SN
                          statusMap[cleanBaseSN] = {
                            ...statusMap[cleanBaseSN],
                            values: { ...(statusMap[cleanBaseSN]?.values || {}), ...values },
                            ...flattenedValues
                          };
                          
                          logger.log(`📊 [DeviceManager] Stored status for ${deviceSN} and ${cleanBaseSN}:`, {
                            online,
                            endpoint: state.endpoint,
                            valueKeys: Object.keys(values),
                            flattenedKeys: Object.keys(flattenedValues)
                          });
                        });
                      }
                    });
                    logger.log(`✅ [DeviceManager] Using MCP data array format, processed ${batchResult.data.length} entries with ${Object.keys(statusMap).length} status entries`);
                  } else {
                    // Direct object format: { "baseSN": { online, ... } }
                    Object.keys(batchResult).forEach(deviceId => {
                      const baseSN = deviceId.split('#')[0];
                      statusMap[baseSN] = batchResult[deviceId];
                    });
                    logger.log(`✅ [DeviceManager] Using direct object format`);
                  }
                  
                  logger.log(`📊 [DeviceManager] Retrieved status for ${Object.keys(statusMap).length} baseSNs`);
                } else {
                  logger.warn(`⚠️ [DeviceManager] Invalid response format:`, batchResult);
                }
              } catch (statusError) {
                logger.error(`❌ [DeviceManager] Failed to query status:`, statusError);
                // Continue without status - devices will show as offline
              }
              
              if (Object.keys(statusMap).length > 0) {
                logger.log(`✅ [DeviceManager] Retrieved status for ${Object.keys(statusMap).length} status entries`);
                
                // ✅ Update device status - try exact deviceSN match first, then baseSN
                const updatedDevices = uniqueDevices.map(device => {
                  const deviceSN = device.sn || (device as any).deviceSN || (device as any).baseSN;
                  const baseSN = deviceSN ? deviceSN.split('#')[0] : '';
                  
                  // ✅ FIX: Try exact deviceSN match first (for endpoints), then fallback to baseSN
                  const deviceStatus = statusMap[deviceSN] || statusMap[baseSN];
                  
                  if (deviceStatus) {
                    // ✅ Extract online status from status result
                    // Check multiple possible fields: online, status, state
                    let isOnline = false;
                    
                    if (deviceStatus.online === true || deviceStatus.online === 1 || deviceStatus.online === 'true') {
                      isOnline = true;
                    } else if (deviceStatus.status === 'online' || deviceStatus.status === 1) {
                      isOnline = true;
                    } else if (deviceStatus.state === 'online' || deviceStatus.state === 'connected' || deviceStatus.state === 'active') {
                      isOnline = true;
                    } else if (deviceStatus.online === false || deviceStatus.online === 0 || deviceStatus.online === 'false') {
                      isOnline = false;
                    } else if (deviceStatus.status === 'offline' || deviceStatus.status === 0 || deviceStatus.status === false) {
                      isOnline = false;
                    } else if (deviceStatus.state === 'offline' || deviceStatus.state === 'disconnected' || deviceStatus.state === 'inactive') {
                      isOnline = false;
                    } else {
                      // If online field is undefined/null, default to offline for safety
                      isOnline = false;
                    }
                    
                    // Debug log for specific devices
                    if (baseSN.includes('UT3MOF') || baseSN.includes('UTU500') || baseSN.includes('UTU32A') || baseSN.includes('142B0002')) {
                      logger.log(`🔍 [DeviceManager] Status for ${deviceSN}:`, {
                        matchedBy: statusMap[deviceSN] ? 'deviceSN' : 'baseSN',
                        online: deviceStatus.online,
                        values: deviceStatus.values ? Object.keys(deviceStatus.values) : [],
                        flattenedValues: Object.keys(deviceStatus).filter(k => !['online', 'baseSN', 'endpoint', 'values', 'status', 'state'].includes(k)),
                        isOnline
                      });
                    }
                    
                    // ✅ FIX: Merge device with status data including values
                    // Only extract sensor values - DO NOT spread statusFields as it contains
                    // system metadata (name, type, displayName, nickname, groupId, groupName)
                    // that would overwrite important device properties from QueryDevices
                    const { 
                      values,
                      // These are the ONLY fields we want from deviceStatus
                      baseSN: statusBaseSN,
                      endpoint: statusEndpoint,
                      // Sensor values (flattened from values)
                      occupancy,
                      lux,
                      temperature,
                      humidity,
                      temp,
                      rh,
                      watt,
                      watt_hour,
                      voltage,
                      current,
                      real_power,
                      apparent_power,
                      power_factor,
                      frequency,
                      presence,
                      // Ignore system metadata: name, type, displayName, nickname, groupId, groupName, etc.
                    } = deviceStatus;
                    
                    return {
                      ...device,
                      status: isOnline ? 'online' as const : 'offline' as const,
                      online: isOnline,
                      values: values || {},
                      // Only spread specific sensor values (not system metadata)
                      ...(occupancy !== undefined && { occupancy }),
                      ...(lux !== undefined && { lux }),
                      ...(temperature !== undefined && { temperature }),
                      ...(humidity !== undefined && { humidity }),
                      ...(temp !== undefined && { temp }),
                      ...(rh !== undefined && { rh }),
                      ...(watt !== undefined && { watt }),
                      ...(watt_hour !== undefined && { watt_hour }),
                      ...(voltage !== undefined && { voltage }),
                      ...(current !== undefined && { current }),
                      ...(real_power !== undefined && { real_power }),
                      ...(apparent_power !== undefined && { apparent_power }),
                      ...(power_factor !== undefined && { power_factor }),
                      ...(frequency !== undefined && { frequency }),
                      ...(presence !== undefined && { presence }),
                    };
                  }
                  
                  // If no status found, default to offline
                  if (baseSN.includes('UT3MOF') || baseSN.includes('UTU500') || baseSN.includes('UTU32A') || baseSN.includes('142B0002')) {
                    logger.warn(`⚠️ [DeviceManager] No status found for ${deviceSN} (baseSN: ${baseSN})`);
                  }
                  
                  return {
                    ...device,
                    status: 'offline' as const,
                    online: false,
                    values: {}
                  };
                });
                
                logger.log(`✅ [DeviceManager] Updated status for ${updatedDevices.length} devices`);
                setAvailableDevices(updatedDevices);
                return;
              } else {
                logger.warn(`⚠️ [DeviceManager] No status data returned from queryStatus`);
              }
            }
          } catch (error) {
            logger.error('❌ [DeviceManager] Failed to query device status:', error);
            // Continue with original devices if status query fails
          }
        }
        
        setAvailableDevices(uniqueDevices);
      }
    } catch (error) {
      logger.error('Failed to load devices:', error);
    } finally {
      setLoading(false);
    }
  };

  // Refresh with full sync - triggers backend to sync from UltronCloud first
  const handleRefreshWithSync = async () => {
    if (!selectedSourceGroup) return;
    setLoading(true);
    try {
      // Step 1: Sync capabilities (device list) from UltronCloud - NEW DEVICES WILL APPEAR
      logger.log('🔄 Step 1: Syncing capabilities from UltronCloud...');
      await ultronTuringApi.syncCapabilities();
      
      // Step 2: Sync shadow state (device states) from UltronCloud
      logger.log('🔄 Step 2: Syncing shadow state from UltronCloud...');
      await ultronTuringApi.syncShadowState(true);
      
      // Step 3: Query fresh status (from the now-synced Shadow)
      logger.log('🔄 Step 3: Querying fresh device status...');
      const freshStatus = await ultronTuringApi.queryStatus(undefined, { locationId: selectedSourceGroup });
      
      // Step 4: Load devices list
      logger.log('🔄 Step 4: Loading devices with fresh status...');
      await loadDevicesFromGroup(selectedSourceGroup);
      
      // Step 5: Update availableDevices with fresh online status
      if (freshStatus?.data && Array.isArray(freshStatus.data)) {
        const statusMap = new Map<string, any>();
        freshStatus.data.forEach((s: any) => {
          // ✅ FIX: Handle nested system structure from QueryStatus API
          // API returns { system: { baseSN, online, ... }, states: [...] }
          const system = s.system || {};
          const key = system.baseSN || s.baseSN || s.deviceSN?.split('#')[0] || s.deviceSN;
          if (key) {
            // Store with the online status from system
            statusMap.set(key, { ...system, ...s });
          }
        });
        
        setAvailableDevices(prev => prev.map(device => {
          const baseSN = device.sn?.split('#')[0] || device.sn;
          const status = statusMap.get(baseSN);
          if (status) {
            return {
              ...device,
              online: status.online === true,
              status: status.online === true ? 'online' as const : 'offline' as const
            };
          }
          return device;
        }));
        
        logger.log(`✅ Refresh complete - updated ${statusMap.size} device statuses`);
      } else {
        logger.log('✅ Refresh complete (no status data to update)');
      }
    } catch (error) {
      logger.error('Failed to refresh with sync:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load when source group changes
  useEffect(() => {
    if (selectedSourceGroup) {
      loadDevicesFromGroup(selectedSourceGroup);
    }
  }, [selectedSourceGroup, currentBoard]);

  // Update imported flag when deviceZones changes
  // This fixes the issue where green checkmarks don't show because deviceZones
  // wasn't loaded when loadDevicesFromGroup initially ran
  useEffect(() => {
    if (deviceZones.length > 0 && availableDevices.length > 0) {
      setAvailableDevices(prev => prev.map(device => ({
        ...device,
        imported: deviceZones.some(group => group.devices.includes(device.sn))
      })));
    }
  }, [deviceZones]);

  // Load imported devices - prioritize Firestore, fallback to localStorage for migration
  const loadImportedDevices = async () => {
    try {
      logger.log(`📦 [LoadImportedDevices] Loading for board: ${currentBoard}`);

      // Priority 1: Try to load from Firestore
      const firestoreDevices = await getImportedDevices(currentBoard as any);

      if (firestoreDevices && firestoreDevices.length > 0) {
        logger.log(`✅ [LoadImportedDevices] Loaded ${firestoreDevices.length} devices from Firestore`);

        // Check if any device is missing nickname - if so, refresh from API
        const missingNickname = firestoreDevices.some((d: any) => d.nickname === undefined || d.nickname === null || d.nickname === 'null');
        if (missingNickname) {
          logger.log(`⚠️ [LoadImportedDevices] Some devices missing nickname, refreshing from API...`);
          // ✅ Fix: Use fallback pattern for deviceSN field (migration may have removed 'sn')
          const deviceSNs = firestoreDevices.map((d: any) => (d as any).deviceSN || d.sn || (d as any).deviceId).filter(Boolean);
          try {
            const apiDevices = await ultronTuringApi.queryDevices(undefined, undefined, { sns: deviceSNs });
            if (apiDevices && apiDevices.length > 0) {
              // ✅ Fix: Build map with both deviceSN and sn keys for lookup
              const apiMap = new Map(apiDevices.map((d: any) => [(d as any).deviceSN || d.sn, d]));
              const updatedDevices = firestoreDevices.map((device: any) => {
                // ✅ Fix: Use fallback pattern for device lookup
                const deviceKey = (device as any).deviceSN || device.sn || (device as any).deviceId;
                const apiDevice = apiMap.get(deviceKey);
                if (apiDevice && (device.nickname === undefined || device.nickname === null || device.nickname === 'null')) {
                  return { ...device, nickname: apiDevice.nickname, displayName: apiDevice.displayName || device.displayName };
                }
                return device;
              });
              // Save updated devices
              await saveImportedDevices(currentBoard as any, updatedDevices);
              localStorage.setItem(`imported-devices-${currentBoard}`, JSON.stringify(updatedDevices));
              setImportedDevices(updatedDevices);
              logger.log(`✅ [LoadImportedDevices] Refreshed nicknames from API`);
              return;
            }
          } catch (e) {
            logger.warn(`⚠️ [LoadImportedDevices] Failed to refresh nicknames:`, e);
          }
        }

        setImportedDevices(firestoreDevices);

        // Sync to localStorage as cache
        const key = `imported-devices-${currentBoard}`;
        localStorage.setItem(key, JSON.stringify(firestoreDevices));
        logger.log(`💾 [LoadImportedDevices] Synced to localStorage cache`);
        return;
      }

      // Firestore is the source of truth - no devices found
      // Note: Auto-migration from localStorage has been removed to prevent 
      // devices from reappearing after being cleared from Firestore
      logger.log(`ℹ️ [LoadImportedDevices] No devices found in Firestore for ${currentBoard}`);
      setImportedDevices([]);
      
      // Clear any stale localStorage cache to keep in sync
      const key = `imported-devices-${currentBoard}`;
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        logger.log(`🧹 [LoadImportedDevices] Cleared stale localStorage cache for ${currentBoard}`);
      }
    } catch (error) {
      logger.error('[LoadImportedDevices] Failed to load imported devices:', error);
      setImportedDevices([]);
    }
  };

  // Sync devices - verify and cleanup invalid devices
  const syncDevices = async (silent = false) => {
    if (!silent) setSyncing(true);
    try {
      // Use Firestore as source of truth (not localStorage)
      const savedDevices = await getImportedDevices(currentBoard as any);
      if (!savedDevices || savedDevices.length === 0) {
        if (!silent) {
          setSyncNotification({ message: 'No devices to sync', type: 'info' });
        }
        return;
      }

      const savedSNs = savedDevices.map((d: any) => (d as any).deviceSN || d.sn).filter(Boolean);

      logger.log(`🔄 [Sync${silent ? ' Auto' : ''}] Starting device sync for board:`, currentBoard);
      logger.log(`🔄 [Sync${silent ? ' Auto' : ''}] Saved devices:`, savedSNs);

      // Query all devices from all user's groups
      let allAvailableDevices: any[] = [];
      if (availableGroups.length > 0) {
        // Query devices from all groups
        for (const group of availableGroups) {
          const groupId = group.id || group.groupId;
          if (groupId) {
            try {
              const devices = await ultronTuringApi.queryDevices(groupId);
              if (devices && Array.isArray(devices)) {
                allAvailableDevices = allAvailableDevices.concat(devices);
              }
            } catch (error) {
              logger.error(`Failed to query group ${groupId}:`, error);
            }
          }
        }
      }

      logger.log(`🔄 [Sync${silent ? ' Auto' : ''}] Total available devices:`, allAvailableDevices.length);

      // ✅ Safety check: If API returned no devices, skip cleanup to prevent data loss
      if (allAvailableDevices.length === 0) {
        logger.warn(`⚠️ [Sync${silent ? ' Auto' : ''}] No available devices from API, skipping cleanup to prevent data loss`);
        if (!silent) {
          setSyncNotification({ message: 'Cannot verify devices - API returned no data', type: 'info' });
          setTimeout(() => setSyncNotification(null), 5000);
        }
        return;
      }

      // ✅ FIX: Use baseSN for comparison to handle format mismatch
      // savedDevices may use: "UTU500-4J2222M#1" (with endpoint)
      // API may return: "UTU500-4J2222M" (baseSN only)
      const getBaseSN = (sn: string) => sn?.split('#')[0] || sn;
      const availableBaseSNs = new Set(allAvailableDevices.map((d: any) => getBaseSN(d.sn)));
      logger.log(`🔄 [Sync${silent ? ' Auto' : ''}] Available baseSNs:`, Array.from(availableBaseSNs));

      // Find devices that no longer exist (compare by baseSN)
      const invalidDevices = savedDevices.filter(d => !availableBaseSNs.has(getBaseSN(d.sn)));
      logger.log(`🔄 [Sync${silent ? ' Auto' : ''}] Invalid devices:`, invalidDevices.map(d => d.sn));
      
      // ✅ Safety check: If would delete >50% of devices, abort (likely SN format issue)
      if (invalidDevices.length > savedDevices.length * 0.5) {
        logger.error(`❌ [Sync${silent ? ' Auto' : ''}] Safety check failed: Would delete ${invalidDevices.length}/${savedDevices.length} (>50%) devices!`);
        if (!silent) {
          setSyncNotification({ message: `Safety check: Would delete too many devices (${invalidDevices.length}/${savedDevices.length})`, type: 'error' });
          setTimeout(() => setSyncNotification(null), 5000);
        }
        return;
      }

      if (invalidDevices.length > 0) {
        // Remove invalid devices (using baseSN comparison)
        const validDevices = savedDevices.filter(d => availableBaseSNs.has(getBaseSN(d.sn)));
        await saveDevicesToStorage(validDevices);

        // Also clean up deviceZones - remove references to deleted devices
        const invalidSNs = new Set(invalidDevices.map(d => d.sn));
        const updatedGroups = deviceZones.map(group => ({
          ...group,
          devices: group.devices.filter(sn => !invalidSNs.has(sn))
        }));
        saveDeviceZones(updatedGroups);
        logger.log(`✅ [Sync${silent ? ' Auto' : ''}] Cleaned up ${invalidDevices.length} device(s) from groups`);

        if (!silent) {
          const removedNames = invalidDevices.map(d => d.nickname || d.displayName || d.name || d.sn).join(', ');
          setSyncNotification({
            message: `Removed ${invalidDevices.length} device(s) that no longer exist: ${removedNames}`,
            type: 'success'
          });
        }
        logger.log(`✅ [Sync${silent ? ' Auto' : ''}] Removed ${invalidDevices.length} invalid devices`);
      } else {
        if (!silent) {
          setSyncNotification({
            message: 'All devices are valid. No cleanup needed.',
            type: 'info'
          });
        }
        logger.log(`✅ [Sync${silent ? ' Auto' : ''}] All devices are valid`);
      }

      // Auto-hide notification after 5 seconds (only for manual sync)
      if (!silent) {
        setTimeout(() => setSyncNotification(null), 5000);
      }

    } catch (error) {
      logger.error(`❌ [Sync${silent ? ' Auto' : ''}] Failed to sync devices:`, error);
      if (!silent) {
        setSyncNotification({
          message: 'Failed to sync devices. Please try again.',
          type: 'error'
        });
        setTimeout(() => setSyncNotification(null), 5000);
      }
    } finally {
      if (!silent) setSyncing(false);
    }
  };

  // Health Check - Verify subscription integrity
  const handleHealthCheck = async (autoHeal: boolean = true) => {
    setIsCheckingHealth(true);
    try {
      logger.log('🏥 [Health Check] Running subscription health check...');

      const result = await runHealthCheck(autoHeal);
      setHealthCheckResult(result);

      // Display summary notification
      const { healthy, warnings, errors, autoHealSuccess, autoHealFailed } = result;
      let message = `Health Check: ${healthy} healthy, ${warnings} warnings, ${errors} errors`;

      if (autoHeal && (autoHealSuccess > 0 || autoHealFailed > 0)) {
        message += ` | Auto-heal: ${autoHealSuccess} fixed, ${autoHealFailed} failed`;
      }

      setSyncNotification({
        message,
        type: errors > 0 ? 'error' : warnings > 0 ? 'info' : 'success'
      });

      // Auto-hide notification after 8 seconds
      setTimeout(() => setSyncNotification(null), 8000);

      logger.log('✅ [Health Check] Complete:', result);

      // Update quick status after health check
      const status = await getQuickStatus();
      setQuickStatus(status);

    } catch (error) {
      logger.error('❌ [Health Check] Failed:', error);
      setSyncNotification({
        message: 'Health check failed. Please try again.',
        type: 'error'
      });
      setTimeout(() => setSyncNotification(null), 5000);
    } finally {
      setIsCheckingHealth(false);
    }
  };

  // Save imported devices to Firestore and localStorage
  const saveDevicesToStorage = async (devices: Device[]): Promise<{ success: boolean; firestoreFailed: boolean }> => {
    let firestoreFailed = false;

    logger.log(`💾 [SaveImportedDevices] Saving ${devices.length} devices for ${currentBoard}`);

    // 1. Try to save to Firestore (primary storage)
    try {
      await saveImportedDevices(currentBoard as any, devices);
      logger.log(`✅ [SaveImportedDevices] Saved to Firestore`);
    } catch (firestoreError) {
      firestoreFailed = true;
      logger.error('❌ [SaveImportedDevices] Firestore save FAILED:', firestoreError);

      // Show user notification about Firestore failure
      setSyncNotification({
        message: '⚠️ Cloud sync failed. Changes saved locally only. Please check your connection.',
        type: 'error'
      });
      setTimeout(() => setSyncNotification(null), 6000);
    }

    // 2. Save to localStorage (cache) - always do this
    try {
      const key = `imported-devices-${currentBoard}`;
      localStorage.setItem(key, JSON.stringify(devices));
      logger.log(`💾 [SaveImportedDevices] Synced to localStorage cache`);
    } catch (localError) {
      logger.error('❌ [SaveImportedDevices] localStorage save FAILED:', localError);

      // Show different notification for localStorage failure
      setSyncNotification({
        message: '❌ Failed to save changes. Please try again.',
        type: 'error'
      });
      setTimeout(() => setSyncNotification(null), 5000);

      return { success: false, firestoreFailed };
    }

    // 3. Update state
    setImportedDevices(devices);

    // 4. Cleanup orphaned device references from deviceOrder and selectedDevices
    try {
      const validDeviceSNs = new Set(devices.map(d => d.sn));
      const cleanedCount = await cleanupOrphanedDeviceReferences(currentBoard as any, validDeviceSNs);
      if (cleanedCount > 0) {
        logger.log(`🧹 Cleaned up ${cleanedCount} orphaned references for ${currentBoard}`);
      }
    } catch (cleanupError) {
      logger.error('⚠️ Cleanup failed (non-critical):', cleanupError);
      // Continue - cleanup failure shouldn't break device saving
    }

    // 5. Update deviceOrder in localStorage (legacy board-specific orders removed)
    // IntelliSpace uses intelliSpaceSelections.deviceOrder in Firestore

    // 6. Notify App.tsx that imported devices have changed (for Battery Alert monitoring)
    window.dispatchEvent(new CustomEvent('imported-devices-changed', {
      detail: { board: currentBoard, devices }
    }));

    if (firestoreFailed) {
      logger.log(`⚠️ [SaveImportedDevices] Completed with Firestore failure (localStorage OK)`);
    } else {
      logger.log(`✅ [SaveImportedDevices] All saves completed successfully`);
    }

    return { success: true, firestoreFailed };
  };

  // Handle select all devices
  const handleSelectAll = () => {
    const unimportedDevices = filteredAvailableDevices.filter(d => !d.imported);
    const unimportedSNs = unimportedDevices.map(d => d.sn);

    // Check if all unimported devices are already selected
    const allSelected = unimportedSNs.every(sn => selectedDevices.includes(sn));

    if (allSelected) {
      // If all are selected, deselect all unimported devices
      setSelectedDevices(selectedDevices.filter(sn => !unimportedSNs.includes(sn)));
    } else {
      // Select all unimported devices (keep any other selections)
      const combinedArray = [...selectedDevices, ...unimportedSNs];
      const uniqueArray = Array.from(new Set(combinedArray));
      setSelectedDevices(uniqueArray);
    }
  };

  // Check if all unimported devices are selected
  const isAllSelected = () => {
    const unimportedDevices = filteredAvailableDevices.filter(d => !d.imported);
    const unimportedSNs = unimportedDevices.map(d => d.sn);
    return unimportedSNs.length > 0 &&
           unimportedSNs.every(sn => selectedDevices.includes(sn));
  };

  // Import selected devices
  const handleImportDevices = async () => {
    const devicesToImport = availableDevices.filter(d => selectedDevices.includes(d.sn));
    const newImported = [...importedDevices];

    // Add devices to imported list
    devicesToImport.forEach(device => {
      if (!newImported.some(d => d.sn === device.sn)) {
        const importedDevice = { ...device, imported: true };
        logger.log(`📥 Importing ${device.sn} with groupName: ${importedDevice.groupName}`);
        newImported.push(importedDevice);
      }
    });

    // Query battery information for imported devices
    logger.log('🔋 Querying battery information for imported devices...');
    try {
      const deviceSNs = devicesToImport.map(d => d.sn);
      const statusResult = await ultronTuringApi.queryStatus(deviceSNs, { detailed: true });

      if (statusResult?.data && Array.isArray(statusResult.data)) {
        logger.log('🔋 Got status data for', statusResult.data.length, 'devices');

        // Extract battery information from status data
        statusResult.data.forEach((statusItem: any) => {
          const deviceSn = statusItem.device || statusItem.deviceId || statusItem.sn;
          const deviceIndex = newImported.findIndex(d => d.sn === deviceSn);

          if (deviceIndex >= 0) {
            let batteryLevel: number | undefined = undefined;

            // Extract battery level using same logic as ClimateDashboard
            // Check ULTRON_SENSOR for battery level (power field)
            if (statusItem.attributes?.ULTRON_SENSOR?.events?.[0]?.data?.power?.[0] !== undefined) {
              batteryLevel = Math.round(statusItem.attributes.ULTRON_SENSOR.events[0].data.power[0]);
              logger.log(`🔋 ${deviceSn}: Found battery in ULTRON_SENSOR = ${batteryLevel}%`);
            }

            // Check attributes['Ultron Device'].battery for battery level (alternative)
            if (batteryLevel === undefined && statusItem.attributes?.['Ultron Device']?.battery !== undefined) {
              batteryLevel = statusItem.attributes['Ultron Device'].battery;
              logger.log(`🔋 ${deviceSn}: Found battery in Ultron Device = ${batteryLevel}%`);
            }

            // Direct properties (fallback)
            if (batteryLevel === undefined && typeof statusItem.batteryLevel === 'number') {
              batteryLevel = statusItem.batteryLevel;
              logger.log(`🔋 ${deviceSn}: Found battery in direct property = ${batteryLevel}%`);
            }

            // Check sensorData object (alternative structure)
            if (batteryLevel === undefined && statusItem.sensorData?.batteryLevel !== undefined) {
              batteryLevel = statusItem.sensorData.batteryLevel;
              logger.log(`🔋 ${deviceSn}: Found battery in sensorData = ${batteryLevel}%`);
            }

            // If battery level found, store it
            if (batteryLevel !== undefined) {
              newImported[deviceIndex] = {
                ...newImported[deviceIndex],
                batteryLevel: batteryLevel,
                hasBattery: true
              };
              logger.log(`✅ ${deviceSn}: Stored battery level = ${batteryLevel}%`);
            } else {
              logger.log(`⚠️ ${deviceSn}: No battery information found in status data`);
            }
          }
        });
      } else {
        logger.warn('⚠️ No status data returned from queryStatus');
      }
    } catch (error) {
      logger.error('❌ Failed to query battery information:', error);
      // Continue with import even if battery query fails
    }

    logger.log('💾 Saving imported devices:', newImported.map(d => ({
      sn: d.sn,
      groupName: d.groupName,
      batteryLevel: (d as any).batteryLevel,
      hasBattery: (d as any).hasBattery
    })));
    await saveDevicesToStorage(newImported);
    setSelectedDevices([]);

    // ✅ Layer 1 + Layer 2: Full sync all imported devices
    // Layer 1: Syncs ALL importedDevices to _app/config/settings.webhook_devices (UltronCloud → Turing Server)
    // Layer 2: Auto-enables WebSocket for all imported devices (Turing Server → Web App)
    try {
      logger.log('🔄 [Sync] Starting full webhook + WebSocket sync after device import...');
      await ultronTuringApi.syncAllImportedDevicesToWebhook();
      logger.log(`✅ [Sync] Full webhook + WebSocket sync completed`);
    } catch (error) {
      logger.error('⚠️ Failed to sync subscriptions:', error);
      // Continue even if sync fails - devices are still imported
    }

    // Update available devices to reflect the change
    setAvailableDevices(prevDevices =>
      prevDevices.map(device => ({
        ...device,
        imported: newImported.some(d => d.sn === device.sn)
      }))
    );

    // Notify parent
    onDeviceSelectionChange?.(newImported.map(d => d.sn));
  };

  // Remove imported device
  const handleRemoveImportedDevice = async (sn: string) => {
    const newImported = importedDevices.filter(d => d.sn !== sn);
    await saveDevicesToStorage(newImported);
    onDeviceSelectionChange?.(newImported.map(d => d.sn));

    // ✅ Remove device from all deviceZones
    const baseSN = sn.split('#')[0];
    const updatedZones = deviceZones.map(zone => ({
      ...zone,
      devices: zone.devices.filter(deviceSN => {
        const deviceBaseSN = deviceSN.split('#')[0];
        return deviceSN !== sn && deviceBaseSN !== baseSN;
      })
    }));
    await saveDeviceZones(updatedZones);
    setDeviceZones(updatedZones);
    logger.log(`🗑️ [RemoveDevice] Removed ${sn} from all deviceZones`);

    // ✅ Clean up intelliSpaceSelections for removed device
    try {
      const { getIntelliSpaceSelections, saveIntelliSpaceSelections } = await import('../services/userPreferencesApi');
      const currentSelections = await getIntelliSpaceSelections();
      
      const baseSN = sn.split('#')[0];
      const filterDevices = (devices: string[] | undefined) => {
        if (!devices) return [];
        return devices.filter(deviceSN => {
          const deviceBaseSN = deviceSN.split('#')[0];
          return deviceSN !== sn && deviceBaseSN !== baseSN;
        });
      };
      
      const updatedSelections = {
        powerMeters: filterDevices(currentSelections.powerMeters),
        switches: filterDevices(currentSelections.switches),
        hvac: filterDevices(currentSelections.hvac),
        thSensors: filterDevices(currentSelections.thSensors),
        lights: filterDevices(currentSelections.lights),
        deviceOrder: filterDevices(currentSelections.deviceOrder),
        excludedRankingDevices: filterDevices(currentSelections.excludedRankingDevices)
      };
      
      await saveIntelliSpaceSelections(updatedSelections);
      logger.log(`🗑️ [RemoveDevice] Cleaned up intelliSpaceSelections for ${sn}`);
    } catch (error) {
      logger.warn('⚠️ [RemoveDevice] Failed to cleanup intelliSpaceSelections:', error);
    }

    // ✅ Layer 1 + Layer 2: Full sync all remaining imported devices
    // Layer 1: Syncs ALL remaining importedDevices to webhook (UltronCloud → Turing Server)
    // Layer 2: Auto-syncs WebSocket subscriptions (Turing Server → Web App)
    try {
      logger.log('🔄 [Sync] Starting full webhook + WebSocket sync after device removal...');
      await ultronTuringApi.syncAllImportedDevicesToWebhook();
      logger.log(`✅ [Sync] Full sync completed (removed ${sn})`);
    } catch (error) {
      logger.error(`⚠️ [Sync] Failed to sync after removing ${sn}:`, error);
      // Continue even if sync fails
    }

    // Update available devices to reflect the change
    setAvailableDevices(prevDevices =>
      prevDevices.map(device => ({
        ...device,
        imported: newImported.some(d => d.sn === device.sn)
      }))
    );
  };

  // Load device groups from Firestore via User Preferences API
  const loadDeviceZones = async () => {
    try {
      const groups = await getVirtualZones(currentBoard as any);
      // Map API groups to local DeviceZone type with required fields
      const mappedGroups: DeviceZone[] = groups.map((g: DeviceZone) => ({
        ...g,
        type: (g.type || 'manual') as 'manual' | 'smart' | 'auto' // Ensure type is set with default value
      }));
      setDeviceZones(mappedGroups);
      logger.log(`✅ Loaded ${groups.length} virtual groups for ${currentBoard}`);
      
      // Also load device control groups (Apple Home style)
      const controlGroups = await getDeviceControlGroups();
      setDeviceControlGroups(controlGroups);
      logger.log(`✅ Loaded ${controlGroups.length} device control groups`);
      
      // ✅ 優化：deviceZones 載入完成後立即觸發 deviceShadow 查詢（不等待 importedDevices）
      // 這樣可以並行載入，減少等待時間
      if (mappedGroups.length > 0) {
        const groupSNs = mappedGroups
          .flatMap(group => group.devices || [])
          .filter((sn): sn is string => !!sn);
        
        if (groupSNs.length > 0) {
          // 觸發 deviceShadow 查詢（useEffect 會自動處理）
          logger.log(`⚡ [DeviceShadow] Triggering early load for ${groupSNs.length} devices from groups`);
        }
      }
    } catch (error) {
      logger.error('Failed to load device groups:', error);
      setDeviceZones([]);
    }
  };

  // Save device groups to Firestore via User Preferences API
  const saveDeviceZones = async (groups: DeviceZone[]) => {
    try {
      await saveVirtualZones(currentBoard as any, groups);
      setDeviceZones(groups);
      logger.log(`✅ Saved ${groups.length} virtual groups for ${currentBoard}`);
    } catch (error) {
      logger.error('Failed to save device groups:', error);
      // Still update local state even if save fails
      setDeviceZones(groups);
    }
  };

  // Create new group
  const handleCreateGroup = () => {
    if (!newGroupName.trim()) return;

    const newGroup: DeviceZone = {
      id: `group-${Date.now()}`,
      name: newGroupName,
      type: 'manual', // Always create manual groups
      devices: [],
      createdAt: new Date().toISOString()
    };

    saveDeviceZones([...deviceZones, newGroup]);
    setNewGroupName('');
    setShowCreateGroup(false);
  };

  // Handle group selection for batch import
  // ✅ Single-zone constraint: Only allow selecting ONE zone at a time
  const handleGroupSelection = (groupId: string) => {
    setSelectedGroups(prev =>
      prev.includes(groupId)
        ? [] // Deselect if already selected
        : [groupId] // Replace with single selection (not append)
    );
  };

  // Import devices and assign to selected groups in one action
  const handleImportToGroups = useCallback(async () => {
    // Validation
    if (selectedForGroup.length === 0) {
      logger.warn('⚠️ No devices selected for import');
      return;
    }
    if (selectedGroups.length === 0) {
      logger.warn('⚠️ No groups selected for assignment');
      return;
    }

    logger.log(`📥 Starting import: ${selectedForGroup.length} device(s) → ${selectedGroups.length} group(s)`);

    try {
      // Step 1: Import devices
      const devicesToImport = availableDevices.filter(d => selectedForGroup.includes(d.sn));
      const newImported = [...importedDevices];

      // Add devices to imported list
      devicesToImport.forEach(device => {
        if (!newImported.some(d => d.sn === device.sn)) {
          const importedDevice = { ...device, imported: true };
          logger.log(`📥 Importing ${device.sn} with groupName: ${importedDevice.groupName}`);
          newImported.push(importedDevice);
        }
      });

      // Step 2: Query battery information for imported devices
      logger.log('🔋 Querying battery information for imported devices...');
      try {
        const deviceSNs = devicesToImport.map(d => d.sn);
        const statusResult = await ultronTuringApi.queryStatus(deviceSNs, { detailed: true });

        if (statusResult?.data && Array.isArray(statusResult.data)) {
          logger.log('🔋 Got status data for', statusResult.data.length, 'devices');

          // Extract battery information from status data
          statusResult.data.forEach((statusItem: any) => {
            const deviceSn = statusItem.device || statusItem.deviceId || statusItem.sn;
            const deviceIndex = newImported.findIndex(d => d.sn === deviceSn);

            if (deviceIndex >= 0) {
              let batteryLevel: number | undefined = undefined;

              // Extract battery level using same logic as ClimateDashboard
              if (statusItem.attributes?.ULTRON_SENSOR?.events?.[0]?.data?.power?.[0] !== undefined) {
                batteryLevel = Math.round(statusItem.attributes.ULTRON_SENSOR.events[0].data.power[0]);
                logger.log(`🔋 ${deviceSn}: Found battery in ULTRON_SENSOR = ${batteryLevel}%`);
              }

              if (batteryLevel === undefined && statusItem.attributes?.['Ultron Device']?.battery !== undefined) {
                batteryLevel = statusItem.attributes['Ultron Device'].battery;
                logger.log(`🔋 ${deviceSn}: Found battery in Ultron Device = ${batteryLevel}%`);
              }

              if (batteryLevel === undefined && typeof statusItem.batteryLevel === 'number') {
                batteryLevel = statusItem.batteryLevel;
                logger.log(`🔋 ${deviceSn}: Found battery in direct property = ${batteryLevel}%`);
              }

              if (batteryLevel === undefined && statusItem.sensorData?.batteryLevel !== undefined) {
                batteryLevel = statusItem.sensorData.batteryLevel;
                logger.log(`🔋 ${deviceSn}: Found battery in sensorData = ${batteryLevel}%`);
              }

              // If battery level found, store it
              if (batteryLevel !== undefined) {
                newImported[deviceIndex] = {
                  ...newImported[deviceIndex],
                  batteryLevel: batteryLevel,
                  hasBattery: true
                };
                logger.log(`✅ ${deviceSn}: Stored battery level = ${batteryLevel}%`);
              } else {
                logger.log(`⚠️ ${deviceSn}: No battery information found in status data`);
              }
            }
          });
        } else {
          logger.warn('⚠️ No status data returned from queryStatus');
        }
      } catch (error) {
        logger.error('❌ Failed to query battery information:', error);
        // Continue with import even if battery query fails
      }

      // Step 3: Save imported devices
      logger.log('💾 Saving imported devices:', newImported.map(d => ({
        sn: d.sn,
        groupName: d.groupName,
        batteryLevel: (d as any).batteryLevel,
        hasBattery: (d as any).hasBattery
      })));
      await saveDevicesToStorage(newImported);

      // Step 4: Assign devices to selected groups
      // ✅ Single-zone constraint: A device can only belong to ONE zone
      // First remove devices from any existing zones, then add to the target zone
      const deviceSNsToAssign = devicesToImport.map(d => d.sn);
      
      // ✅ Only allow ONE target zone (use the first selected group)
      const targetGroupId = selectedGroups[0];
      if (selectedGroups.length > 1) {
        logger.warn(`⚠️ Multiple zones selected. Using only the first zone (single-zone constraint).`);
      }
      
      const updatedGroups = deviceZones.map(group => {
        if (group.id === targetGroupId) {
          // Add devices to target group (avoid duplicates)
          const uniqueDevices = Array.from(new Set([...group.devices, ...deviceSNsToAssign]));
          logger.log(`📋 Adding ${deviceSNsToAssign.length} devices to group "${group.name}"`);
          return { ...group, devices: uniqueDevices };
        } else {
          // ✅ Remove devices from other groups (single-zone constraint)
          const filteredDevices = group.devices.filter(sn => !deviceSNsToAssign.includes(sn));
          if (filteredDevices.length !== group.devices.length) {
            logger.log(`🔄 Removed ${group.devices.length - filteredDevices.length} device(s) from group "${group.name}" (single-zone constraint)`);
          }
          return { ...group, devices: filteredDevices };
        }
      });

      // Save updated groups
      await saveDeviceZones(updatedGroups);

      // Step 5: ✅ Layer 1 + Layer 2: Full sync all imported devices
      // Layer 1: Syncs ALL importedDevices to webhook (UltronCloud → Turing Server)
      // Step 5: 直接訂閱剛匯入的設備到 webhook (Layer 1)
      // 使用 baseSN 格式，直接呼叫 SubscribeDevices
      try {
        const baseSNsToSubscribe = devicesToImport.map(d => {
          const sn = (d as any).deviceSN || d.sn || (d as any).deviceId;
          return sn ? sn.split('#')[0] : null;
        }).filter(Boolean) as string[];
        
        if (baseSNsToSubscribe.length > 0) {
          await ultronTuringApi.subscribeDevices(baseSNsToSubscribe);
          logger.log(`✅ [Import] Subscribed ${baseSNsToSubscribe.length} devices to webhook:`, baseSNsToSubscribe);
        }
      } catch (error) {
        logger.error('⚠️ Failed to subscribe devices to webhook:', error);
      }
      
      // Step 5.1: 同步 websocketEnabledDevices (Layer 2)
      try {
        const { setWebsocketEnabledDevices, getImportedDevices } = await import('../services/userPreferencesApi');
        const allImported = await getImportedDevices('intelliSpace');
        const allBaseSNs = allImported.map(d => {
          const sn = (d as any).deviceSN || d.sn || (d as any).deviceId;
          return sn ? sn.split('#')[0] : null;
        }).filter(Boolean) as string[];
        await setWebsocketEnabledDevices(allBaseSNs);
        logger.log(`✅ [Import] Updated websocketEnabledDevices: ${allBaseSNs.length} devices`);
      } catch (error) {
        logger.error('⚠️ Failed to sync websocketEnabledDevices:', error);
      }

      // Step 5.5: ✅ Battery alert info (server handles everything)
      // Global battery alert (alertType: 'battery') dynamically queries ALL devices
      // Server automatically includes new devices on next check - no sync needed here
      try {
        const batterySettings = await getBatteryAlert();
        if (batterySettings?.enabled) {
          logger.log(`🔋 [BatteryAlert] Global battery alert is enabled - new devices will be included on next server check`);
        }
      } catch (error) {
        // Ignore - non-critical
      }

      // Step 6: Update available devices to reflect the change
      // ✅ Use updatedGroups (the just-saved groups) to check if device is in any virtual group
      setAvailableDevices(prevDevices =>
        prevDevices.map(device => ({
          ...device,
          imported: updatedGroups.some(group => group.devices.includes(device.sn))
        }))
      );

      // Step 7: Clear selections
      setSelectedForGroup([]);
      setSelectedGroups([]);

      logger.log(`✅ Successfully imported ${devicesToImport.length} device(s) to ${selectedGroups.length} group(s)`);

      // Step 8: Notify Dashboard to refresh devices
      if (onDevicesImported) {
        logger.log('🔄 Triggering Dashboard refresh after import...');
        onDevicesImported();
      }
    } catch (error) {
      logger.error('❌ Failed to import devices to groups:', error);
    }
  }, [selectedForGroup, selectedGroups, availableDevices, importedDevices, deviceZones, saveDevicesToStorage, saveDeviceZones, syncWebhookDevices, setAvailableDevices, setSelectedForGroup, setSelectedGroups, onDevicesImported]);

  // Add devices to group
  // ✅ Single-zone constraint: Remove devices from other groups before adding
  const handleAddDevicesToGroup = (groupId: string, deviceSNs: string[]) => {
    const updatedGroups = deviceZones.map(group => {
      if (group.id === groupId) {
        // Add to target group
        const uniqueDevices = Array.from(new Set([...group.devices, ...deviceSNs]));
        return { ...group, devices: uniqueDevices };
      } else {
        // ✅ Remove from other groups (single-zone constraint)
        const filteredDevices = group.devices.filter(sn => !deviceSNs.includes(sn));
        return { ...group, devices: filteredDevices };
      }
    });
    saveDeviceZones(updatedGroups);
  };

  // Remove device from group
  const handleRemoveDeviceFromGroup = async (groupId: string, deviceSN: string) => {
    try {
      // 1. Remove device from virtual group
      const updatedGroups = deviceZones.map(group => {
        if (group.id === groupId) {
          return { ...group, devices: group.devices.filter(d => d !== deviceSN) };
        }
        return group;
      });
      await saveDeviceZones(updatedGroups);

      // 2. Check if device is in ANY other virtual groups (same board)
      const isDeviceInOtherGroups = updatedGroups.some(group =>
        group.devices.includes(deviceSN)
      );

      // 3. If NOT in any group of this board, remove from this board's importedDevices
      if (!isDeviceInOtherGroups) {
        // Remove from this board's imported devices
        const newImported = importedDevices.filter(d => d.sn !== deviceSN);
        await saveDevicesToStorage(newImported);

        // Update Available Devices UI (remove green checkmark)
        // ✅ Use updatedGroups to check if device is in any virtual group
        setAvailableDevices(prevDevices =>
          prevDevices.map(device => ({
            ...device,
            imported: updatedGroups.some(group => group.devices.includes(device.sn))
          }))
        );

        // 3.5. Clean up intelliSpaceSelections for removed device
        try {
          const { getIntelliSpaceSelections, saveIntelliSpaceSelections } = await import('../services/userPreferencesApi');
          const currentSelections = await getIntelliSpaceSelections();
          
          const baseSN = deviceSN.split('#')[0];
          const filterDevices = (devices: string[] | undefined) => {
            if (!devices) return [];
            return devices.filter(sn => {
              const snBaseSN = sn.split('#')[0];
              return sn !== deviceSN && snBaseSN !== baseSN;
            });
          };
          
          const updatedSelections = {
            powerMeters: filterDevices(currentSelections.powerMeters),
            switches: filterDevices(currentSelections.switches),
            hvac: filterDevices(currentSelections.hvac),
            thSensors: filterDevices(currentSelections.thSensors),
            lights: filterDevices(currentSelections.lights),
            deviceOrder: filterDevices(currentSelections.deviceOrder),
            excludedRankingDevices: filterDevices(currentSelections.excludedRankingDevices)
          };
          
          await saveIntelliSpaceSelections(updatedSelections);
          logger.log(`🗑️ [RemoveDeviceFromGroup] Cleaned up intelliSpaceSelections for ${deviceSN}`);
        } catch (error) {
          logger.warn('⚠️ [RemoveDeviceFromGroup] Failed to cleanup intelliSpaceSelections:', error);
        }

        // 4. Check if device is used in OTHER boards before removing from webhook
        const isUsedInOtherBoards = await isDeviceUsedAnywhereElse(deviceSN, currentBoard);

        if (!isUsedInOtherBoards) {
          // Device is NOT used anywhere else - safe to remove from all subscriptions
          logger.log(`🗑️ Device ${deviceSN} is not used in any board, removing from all subscriptions`);

          // Layer 1 + Layer 2: Full sync (removes device from both webhook and WebSocket)
          await ultronTuringApi.syncAllImportedDevicesToWebhook();

          setSyncNotification({
            message: `✅ 設備已從所有群組移除並清理訂閱`,
            type: 'success'
          });
        } else {
          // Device is still used in other boards - keep subscriptions
          logger.log(`📋 Device ${deviceSN} is still used in other boards, keeping subscriptions`);

          // Still need to sync to ensure consistency (device won't be removed since it's in other boards)
          await ultronTuringApi.syncAllImportedDevicesToWebhook();

          setSyncNotification({
            message: `✅ 設備已從當前 Board 移除（其他 Board 仍在使用）`,
            type: 'success'
          });
        }
        setTimeout(() => setSyncNotification(null), 5000);
      } else {
        setSyncNotification({
          message: `✅ 設備已從群組移除（仍在其他群組中）`,
          type: 'success'
        });
        setTimeout(() => setSyncNotification(null), 5000);
      }

      // Refresh device groups display
      setDeviceZones(updatedGroups);

    } catch (error) {
      logger.error('Failed to remove device from group:', error);
      setSyncNotification({
        message: `❌ 移除設備失敗`,
        type: 'error'
      });
      setTimeout(() => setSyncNotification(null), 5000);
    }
  };

  // Reorder devices within a zone (drag-and-drop)
  const handleReorderDevices = async (zoneId: string, newDeviceOrder: string[]) => {
    try {
      // Update local state immediately for responsive UI
      const updatedZones = deviceZones.map(zone => {
        if (zone.id === zoneId) {
          return { ...zone, devices: newDeviceOrder };
        }
        return zone;
      });
      setDeviceZones(updatedZones);

      // Notify parent for real-time sync
      onZonesChange?.(updatedZones);

      // Save to Firestore
      await saveDeviceZones(updatedZones);
      logger.log(`✅ [ReorderDevices] Zone ${zoneId} device order updated:`, newDeviceOrder);
    } catch (error) {
      logger.error('Failed to reorder devices:', error);
      // Revert to original order on error
      const zones = await getVirtualZones(currentBoard);
      setDeviceZones(zones);
    }
  };

  // Ungroup a device control group (Apple Home style)
  const handleUngroupControlGroup = async (groupId: string) => {
    try {
      await deleteDeviceControlGroup(groupId);
      // Reload control groups
      const updatedGroups = await getDeviceControlGroups();
      setDeviceControlGroups(updatedGroups);
      logger.log(`✅ [UngroupControlGroup] Group ${groupId} deleted`);
    } catch (error) {
      logger.error('Failed to ungroup control group:', error);
    }
  };

  // Toggle multi-channel display mode for a device
  const handleToggleMultiChannelDisplayMode = async (baseSN: string) => {
    try {
      const currentMode = multiChannelDisplayModes[baseSN] || 'merged';
      const newMode = currentMode === 'merged' ? 'expanded' : 'merged';
      
      // Update local state immediately for responsive UI
      setMultiChannelDisplayModes(prev => ({
        ...prev,
        [baseSN]: newMode
      }));
      
      // Save to Firestore
      await setMultiChannelDisplayMode(baseSN, newMode);
      
      // Refresh device list to apply new display mode
      if (selectedSourceGroup) {
        // Need to pass the updated modes since state may not be updated yet
        const updatedModes: Record<string, 'merged' | 'expanded'> = { ...multiChannelDisplayModes, [baseSN]: newMode };
        const deviceList = await ultronTuringApi.queryDevices(selectedSourceGroup, undefined, { 
          detailed: true,
          multiChannelDisplayModes: updatedModes
        });
        
        if (deviceList && Array.isArray(deviceList)) {
          // Re-format devices with updated display mode
          const formattedDevices: Device[] = deviceList
            .filter((device: any) => device && (device.sn || device.deviceSN || device.baseSN))
            .map((device: any) => {
              const deviceSn = device.sn || device.deviceSN || device.baseSN;
              return {
                ...device,
                sn: deviceSn,
                imported: deviceZones.some(group => group.devices.includes(deviceSn))
              } as Device;
            });
          setAvailableDevices(formattedDevices);
        }
      }
      
      logger.log(`🔄 Toggled display mode for ${baseSN}: ${currentMode} → ${newMode}`);
    } catch (error) {
      logger.error('Failed to toggle multi-channel display mode:', error);
    }
  };

  // Remove entire group (with proper cleanup of orphaned devices)
  const handleRemoveGroup = async (groupId: string) => {
    try {
      // 1. Find the group being deleted and get its devices
      const groupToDelete = deviceZones.find(group => group.id === groupId);
      if (!groupToDelete) {
        logger.warn(`⚠️ Group ${groupId} not found`);
        return;
      }
      
      const devicesInDeletedGroup = groupToDelete.devices;
      logger.log(`🗑️ [RemoveGroup] Deleting group "${groupToDelete.name}" with ${devicesInDeletedGroup.length} devices`);
      
      // 2. Remove the group from deviceZones
      const updatedGroups = deviceZones.filter(group => group.id !== groupId);
      await saveDeviceZones(updatedGroups);
      setDeviceZones(updatedGroups);
      
      // 3. Find orphaned devices (devices not in any other group)
      const orphanedDevices: string[] = [];
      for (const deviceSN of devicesInDeletedGroup) {
        const isInOtherGroup = updatedGroups.some(group => group.devices.includes(deviceSN));
        if (!isInOtherGroup) {
          orphanedDevices.push(deviceSN);
        }
      }
      
      logger.log(`🔍 [RemoveGroup] Found ${orphanedDevices.length} orphaned devices: ${orphanedDevices.join(', ') || '(none)'}`);
      
      // 4. Remove orphaned devices from importedDevices
      if (orphanedDevices.length > 0) {
        const newImported = importedDevices.filter(d => {
          const deviceSN = (d as any).deviceSN || d.sn || (d as any).deviceId;
          return !orphanedDevices.includes(deviceSN);
        });
        await saveDevicesToStorage(newImported);
        
        // Update Available Devices UI
        setAvailableDevices(prevDevices =>
          prevDevices.map(device => ({
            ...device,
            imported: newImported.some(d => d.sn === device.sn)
          }))
        );
        
        logger.log(`🗑️ [RemoveGroup] Removed ${orphanedDevices.length} orphaned devices from importedDevices`);
        
        // 4.5. Remove orphaned devices from intelliSpaceSelections
        try {
          const { getIntelliSpaceSelections, saveIntelliSpaceSelections } = await import('../services/userPreferencesApi');
          const currentSelections = await getIntelliSpaceSelections();
          
          // Create a set of orphaned device SNs (including baseSN for matching)
          const orphanedSet = new Set(orphanedDevices);
          const orphanedBaseSNs = new Set(orphanedDevices.map(sn => sn.split('#')[0]));
          
          // Filter out orphaned devices from each selection category
          const filterDevices = (devices: string[] | undefined) => {
            if (!devices) return [];
            return devices.filter(sn => {
              const baseSN = sn.split('#')[0];
              return !orphanedSet.has(sn) && !orphanedBaseSNs.has(baseSN);
            });
          };
          
          const updatedSelections = {
            powerMeters: filterDevices(currentSelections.powerMeters),
            switches: filterDevices(currentSelections.switches),
            hvac: filterDevices(currentSelections.hvac),
            thSensors: filterDevices(currentSelections.thSensors),
            lights: filterDevices(currentSelections.lights),
            deviceOrder: filterDevices(currentSelections.deviceOrder),
            excludedRankingDevices: filterDevices(currentSelections.excludedRankingDevices)
          };
          
          await saveIntelliSpaceSelections(updatedSelections);
          logger.log(`🗑️ [RemoveGroup] Cleaned up intelliSpaceSelections for ${orphanedDevices.length} devices`);
        } catch (error) {
          logger.warn('⚠️ [RemoveGroup] Failed to cleanup intelliSpaceSelections:', error);
        }
      }
      
      // 5. Sync subscriptions (this will now properly cleanup when importedDevices is empty)
      try {
        logger.log('🔄 [RemoveGroup] Syncing webhook subscriptions...');
        await ultronTuringApi.syncAllImportedDevicesToWebhook();
        logger.log('✅ [RemoveGroup] Webhook subscriptions synced');
      } catch (error) {
        logger.error('⚠️ [RemoveGroup] Failed to sync subscriptions:', error);
      }
      
      setSyncNotification({
        message: `✅ 已刪除群組 "${groupToDelete.name}"${orphanedDevices.length > 0 ? ` 並移除 ${orphanedDevices.length} 個設備` : ''}`,
        type: 'success'
      });
      setTimeout(() => setSyncNotification(null), 5000);
      
      logger.log(`✅ [RemoveGroup] Completed: group "${groupToDelete.name}" deleted`);
    } catch (error) {
      logger.error('❌ [RemoveGroup] Failed to remove group:', error);
      setSyncNotification({
        message: '❌ 刪除群組失敗',
        type: 'error'
      });
      setTimeout(() => setSyncNotification(null), 5000);
    }
  };

  // Rename group
  const handleRenameGroup = (groupId: string, newName: string) => {
    if (!newName.trim()) {
      setRenamingGroupId(null);
      setTempGroupName('');
      return;
    }
    
    const updatedGroups = deviceZones.map(group => 
      group.id === groupId 
        ? { ...group, name: newName.trim() }
        : group
    );
    saveDeviceZones(updatedGroups);
    setRenamingGroupId(null);
    setTempGroupName('');
    logger.log(`✅ Renamed group ${groupId} to "${newName.trim()}"`);
  };

  // Start renaming a group
  const handleStartRename = useCallback((groupId: string, currentName: string) => {
    setRenamingGroupId(groupId);
    setTempGroupName(currentName);
  }, []);

  // Cancel renaming
  const handleCancelRename = useCallback(() => {
    setRenamingGroupId(null);
    setTempGroupName('');
  }, []);

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const deviceSN = event.active.id as string;
    const device = importedDevices.find(d => d.sn === deviceSN);
    if (device) {
      setActiveDevice(device);
    }
  };

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    // Immediately clear drag overlay to prevent bounce-back animation
    setActiveDevice(null);

    if (over && over.id) {
      const activeSN = active.id as string;
      const overSN = over.id as string;

      // Check if dropped over a group
      const targetGroup = deviceZones.find(g => g.id === overSN);
      if (targetGroup) {
        // Add device to group
        handleAddDevicesToGroup(overSN, [activeSN]);
        logger.log(`✅ Device ${activeSN} added to group ${targetGroup.name}`);
      } else {
        // Check if reordering within imported devices
        const activeIndex = importedDevices.findIndex(d => d.sn === activeSN);
        const overIndex = importedDevices.findIndex(d => d.sn === overSN);

        if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
          // Reorder devices
          const reorderedDevices = [...importedDevices];
          const [movedDevice] = reorderedDevices.splice(activeIndex, 1);
          reorderedDevices.splice(overIndex, 0, movedDevice);

          await saveDevicesToStorage(reorderedDevices);
          logger.log(`✅ Reordered device ${activeSN} from position ${activeIndex} to ${overIndex}`);
        }
      }
    }
  };

  // Helper function to get meaningful device type based on traits and sensorProfile
  // Maps raw API types (ULTRON_SENSOR, SENSOR) to user-friendly types
  const getDisplayDeviceType = (device: Device): string => {
    const traits = device.traits || [];
    const sn = device.sn || '';
    const rawType = device.type || '';
    
    // 1. If API returns a meaningful type, use it directly
    // These types are already user-friendly and reliable
    const friendlyTypes = ['AC_UNIT', 'FAN', 'LIGHT', 'SWITCH', 'BULB', 'LED_STRIP', 'POWER_METER'];
    if (friendlyTypes.includes(rawType)) {
      return rawType;
    }
    
    // 2. Only for ULTRON_SENSOR or SENSOR types, use traits/SN to determine actual type
    if (rawType === 'ULTRON_SENSOR' || rawType === 'SENSOR') {
      // Check by SN prefix for sensor devices
      // UTU32A: 光照人存感測器 (Presence + Lux sensor)
      if (sn.startsWith('UTU32A')) {
        return 'PRESENCE_LUX_SENSOR';
      }
      // UTU322: 人體感應器 (Presence only)
      if (sn.startsWith('UTU322')) {
        return 'PRESENCE_SENSOR';
      }
      // UTU307, UTU308, UTU31E: 溫溼度感測器
      if (sn.startsWith('UTU307') || sn.startsWith('UTU308') || sn.startsWith('UTU31E')) {
        return 'TH_SENSOR';
      }
      
      // Check by traits
      // Presence/Occupancy sensor
      if (traits.includes('Occupancy') || traits.includes('Detector')) {
        return 'PRESENCE_SENSOR';
      }
      // Temperature & Humidity sensor
      if (traits.includes('HumiditySetting') || 
          (traits.includes('Sensor') && traits.includes('TemperatureControl'))) {
        return 'TH_SENSOR';
      }
      // Generic sensor
      if (traits.includes('Sensor')) {
        return 'SENSOR';
      }
    }
    
    // 3. Fallback to raw type or 'DEVICE'
    return rawType || 'DEVICE';
  };

  // Helper function to get device type labels for all devices
  // Google Material Design style - Returns an array of labels with Material icons
  // Supports multi-labels (e.g., Switch + Meter for devices with power monitoring)
  type DeviceLabel = { 
    Icon: React.ComponentType<{ sx?: object }>; 
    label: string; 
    color: string;
    iconColor: string;
  };
  
  const getDeviceTypeLabels = (device: Device): DeviceLabel[] => {
    const labels: DeviceLabel[] = [];
    const traits = device.traits || [];
    const rawType = device.type || '';
    // Support both formats: normalized sensorProfile (new) or attributes.sensorProfile (old)
    const sensorProfile = (device as any).sensorProfile || (device as any).attributes?.sensorProfile || {};
    
    // ═══════════════════════════════════════════════════════════════════════════
    // PRIORITY 0: Use ACTUAL VALUES from QueryStatus (most accurate)
    // This determines what the device actually reports, not just capabilities
    // ═══════════════════════════════════════════════════════════════════════════
    const statusValues = (device as any).values || {};
    const hasActualValues = Object.keys(statusValues).length > 0;
    
    // Also check flattened values on device object (from loadDevicesFromGroup)
    const hasOccupancyValue = (device as any).occupancy !== undefined || 'occupancy' in statusValues;
    const hasLuxValue = (device as any).lux !== undefined || 'lux' in statusValues;
    const hasTemperatureValue = (device as any).temperature !== undefined || (device as any).temp !== undefined || 'temperature' in statusValues || 'temp' in statusValues;
    const hasHumidityValue = (device as any).humidity !== undefined || (device as any).rh !== undefined || 'humidity' in statusValues || 'rh' in statusValues;
    const hasWattValue = (device as any).watt !== undefined || (device as any).real_power !== undefined || 'watt' in statusValues || 'real_power' in statusValues;
    const hasOnValue = 'on' in statusValues;
    
    // If device has actual status values, use them for classification
    if (hasActualValues || hasOccupancyValue || hasLuxValue || hasTemperatureValue || hasHumidityValue || hasWattValue) {
      const actualLabels: DeviceLabel[] = [];
      
      // ✅ STEP 1: Add PRIMARY device type FIRST (controllable devices)
      // These take priority over sensor readings
      if (rawType === 'DEHUMIDIFIER') {
        actualLabels.push({ Icon: AirIcon, label: 'Dehumidifier', color: 'bg-cyan-500/20 text-cyan-300', iconColor: '#22d3ee' });
      } else if (rawType === 'AC_UNIT') {
        actualLabels.push({ Icon: AcUnitIcon, label: 'AC', color: 'bg-blue-500/20 text-blue-300', iconColor: '#60a5fa' });
      } else if (rawType === 'FAN') {
        actualLabels.push({ Icon: AirIcon, label: 'Fan', color: 'bg-cyan-500/20 text-cyan-300', iconColor: '#22d3d1' });
      } else if (rawType === 'HEATER') {
        actualLabels.push({ Icon: ThermostatIcon, label: 'Heater', color: 'bg-red-500/20 text-red-300', iconColor: '#f87171' });
      } else if (rawType === 'OUTLET' || rawType === 'PLUG') {
        actualLabels.push({ Icon: PowerIcon, label: 'Outlet', color: 'bg-slate-500/20 text-slate-300', iconColor: '#94a3b8' });
      } else if (rawType === 'SWITCH') {
        actualLabels.push({ Icon: ToggleOnIcon, label: 'Switch', color: 'bg-slate-500/20 text-slate-300', iconColor: '#94a3b8' });
      } else if (rawType === 'LIGHT' || rawType === 'BULB') {
        actualLabels.push({ Icon: LightbulbIcon, label: 'Light', color: 'bg-amber-500/20 text-amber-300', iconColor: '#fbbf24' });
      }
      
      // ✅ STEP 2: Add SECONDARY capabilities (sensors)
      // Skip humidity badge for devices that control humidity (dehumidifier, AC)
      const isHumidityController = rawType === 'DEHUMIDIFIER' || rawType === 'AC_UNIT';
      
      // Presence/Occupancy sensor
      if (hasOccupancyValue) {
        actualLabels.push({ Icon: SensorsIcon, label: 'Presence', color: 'bg-purple-500/20 text-purple-300', iconColor: '#a78bfa' });
      }
      
      // Lux sensor
      if (hasLuxValue) {
        actualLabels.push({ Icon: LightModeIcon, label: 'Lux', color: 'bg-amber-500/20 text-amber-300', iconColor: '#fbbf24' });
      }
      
      // TH Sensor (Temperature + Humidity) - but not for humidity controllers
      if (hasTemperatureValue && hasHumidityValue && !isHumidityController) {
        actualLabels.push({ Icon: ThermostatIcon, label: 'TH Sensor', color: 'bg-orange-500/20 text-orange-300', iconColor: '#fb923c' });
      } else if (hasTemperatureValue && !hasHumidityValue) {
        // Temperature only
        actualLabels.push({ Icon: ThermostatIcon, label: 'Temperature', color: 'bg-orange-500/20 text-orange-300', iconColor: '#fb923c' });
      } else if (hasHumidityValue && !hasTemperatureValue && !isHumidityController) {
        // Humidity only (but not for humidity controllers)
        actualLabels.push({ Icon: ThermostatIcon, label: 'Humidity', color: 'bg-cyan-500/20 text-cyan-300', iconColor: '#22d3ee' });
      }
      
      // Power meter
      if (hasWattValue) {
        actualLabels.push({ Icon: ElectricBoltIcon, label: 'Meter', color: 'bg-green-500/20 text-green-300', iconColor: '#4ade80' });
      }
      
      // If we found any labels from actual values, return them
      if (actualLabels.length > 0) {
        return actualLabels;
      }
    }
    
    // ✅ NEW: For merged devices (single/composite), use capabilityTypes to generate all badges
    const capabilityTypes: string[] = (device as any).capabilityTypes || [];
    if (capabilityTypes.length > 0) {
      const allLabels: DeviceLabel[] = [];
      
      // Helper to get label for a capability type
      const getCapabilityLabel = (capType: string): DeviceLabel | null => {
        switch (capType) {
          case 'AC_UNIT':
            return { Icon: AcUnitIcon, label: 'AC', color: 'bg-blue-500/20 text-blue-300', iconColor: '#60a5fa' };
          case 'FAN':
            return { Icon: AirIcon, label: 'Fan', color: 'bg-cyan-500/20 text-cyan-300', iconColor: '#22d3d1' };
          case 'HEATER':
            return { Icon: ThermostatIcon, label: 'Heater', color: 'bg-red-500/20 text-red-300', iconColor: '#f87171' };
          case 'LIGHT':
          case 'BULB':
          case 'LED_STRIP':
            return { Icon: LightbulbIcon, label: 'Light', color: 'bg-amber-500/20 text-amber-300', iconColor: '#fbbf24' };
          case 'SWITCH':
            return { Icon: ToggleOnIcon, label: 'Switch', color: 'bg-slate-500/20 text-slate-300', iconColor: '#94a3b8' };
          case 'OUTLET':
          case 'PLUG':
            return { Icon: PowerIcon, label: 'Outlet', color: 'bg-slate-500/20 text-slate-300', iconColor: '#94a3b8' };
          case 'POWER_METER':
            return { Icon: ElectricBoltIcon, label: 'Meter', color: 'bg-green-500/20 text-green-300', iconColor: '#4ade80' };
          case 'CURTAIN':
            return { Icon: BlindsIcon, label: 'Curtain', color: 'bg-teal-500/20 text-teal-300', iconColor: '#2dd4bf' };
          case 'BLINDS':
          case 'BLIND':
          case 'ROLLER_SHADE':
            return { Icon: BlindsIcon, label: 'Blind', color: 'bg-cyan-500/20 text-cyan-300', iconColor: '#22d3ee' };
          case 'LOCK':
            return { Icon: LockIcon, label: 'Security', color: 'bg-red-500/20 text-red-300', iconColor: '#f87171' };
          case 'CAMERA':
            return { Icon: VideocamIcon, label: 'Camera', color: 'bg-pink-500/20 text-pink-300', iconColor: '#f472b6' };
          case 'SPEAKER':
            return { Icon: SpeakerIcon, label: 'Media', color: 'bg-violet-500/20 text-violet-300', iconColor: '#a78bfa' };
          default:
            return null;
        }
      };
      
      // Add labels for all capability types
      capabilityTypes.forEach(capType => {
        const label = getCapabilityLabel(capType);
        if (label && !allLabels.some(l => l.label === label.label)) {
          allLabels.push(label);
        }
      });
      
      // ✅ Also check sensorProfile for additional capabilities (Meter, TH Sensor, etc.)
      const hasPowerMeter = sensorProfile.watt || sensorProfile.watt_hour || sensorProfile.real_power;
      const hasTempHumidity = sensorProfile.temp || sensorProfile.rh || sensorProfile.temperature || sensorProfile.humidity;
      const hasLux = sensorProfile.lux !== undefined;
      
      if (hasPowerMeter && !allLabels.some(l => l.label === 'Meter')) {
        allLabels.push({ Icon: ElectricBoltIcon, label: 'Meter', color: 'bg-green-500/20 text-green-300', iconColor: '#4ade80' });
      }
      if (hasTempHumidity && !allLabels.some(l => l.label === 'TH Sensor')) {
        allLabels.push({ Icon: ThermostatIcon, label: 'TH Sensor', color: 'bg-orange-500/20 text-orange-300', iconColor: '#fb923c' });
      }
      if (hasLux && !allLabels.some(l => l.label === 'Lux')) {
        allLabels.push({ Icon: LightModeIcon, label: 'Lux', color: 'bg-yellow-500/20 text-yellow-300', iconColor: '#facc15' });
      }
      
      if (allLabels.length > 0) {
        return allLabels;
      }
    }
    
    // === Get detector attributes for distinguishing sensor types ===
    const deviceAttributes = (device as any).attributes || {};
    const deviceDetectorEvents: string[] = deviceAttributes.detectorEvents || [];
    const deviceDetectorStates: string[] = deviceAttributes.detectorStates || [];
    
    // === CAPABILITY FLAGS (calculated once, used later if needed) ===
    const hasPowerMeterCapability = sensorProfile.watt || sensorProfile.watt_hour || 
                                     sensorProfile.voltage || sensorProfile.current ||
                                     sensorProfile.real_power || sensorProfile.apparent_power ||
                                     sensorProfile.reactive_power;
    const hasLuxCapability = sensorProfile.lux !== undefined || traits.includes('Illuminance');
    const hasTempHumidityCapability = sensorProfile.temp || sensorProfile.rh || 
                                       sensorProfile.temperature || sensorProfile.humidity ||
                                       traits.includes('HumiditySetting') || traits.includes('TemperatureControl');
    const hasOccupancyCapability = deviceDetectorStates.includes('occupancy') || 
                                    traits.includes('Occupancy') ||
                                    sensorProfile.occupancy || sensorProfile.presence;
    const hasMotionCapability = deviceDetectorEvents.includes('motionDetected');
    const hasButtonCapability = deviceDetectorEvents.includes('click') || 
                                 deviceDetectorEvents.includes('doubleClick') || 
                                 deviceDetectorEvents.includes('longPress');
    const hasDoorCapability = deviceDetectorEvents.includes('opened') || 
                               deviceDetectorEvents.includes('closed');
    
    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 1: EXPLICIT DEVICE TYPES (Trust API's type classification first)
    // ═══════════════════════════════════════════════════════════════════════════
    
    // --- HVAC Devices ---
    if (rawType === 'AC_UNIT') {
      return [{ Icon: AcUnitIcon, label: 'AC', color: 'bg-blue-500/20 text-blue-300', iconColor: '#60a5fa' }];
    }
    if (rawType === 'FAN') {
      return [{ Icon: AirIcon, label: 'Fan', color: 'bg-cyan-500/20 text-cyan-300', iconColor: '#22d3d1' }];
    }
    if (rawType === 'HEATER') {
      // Floor heating / heater - check endpoint to differentiate
      const endpoint = (device as any).endpoint || (device.sn?.includes('#') ? device.sn.split('#')[1] : '');
      if (endpoint === 'ExternalTemp') {
        // Temperature sensor endpoint
        return [{ Icon: ThermostatIcon, label: 'TH Sensor', color: 'bg-orange-500/20 text-orange-300', iconColor: '#fb923c' }];
      }
      // Controllable heater endpoint
      return [{ Icon: ThermostatIcon, label: 'Heater', color: 'bg-red-500/20 text-red-300', iconColor: '#f87171' }];
    }
    
    // --- Lighting Devices ---
    if (rawType === 'LIGHT' || rawType === 'BULB' || rawType === 'LED_STRIP') {
      return [{ Icon: LightbulbIcon, label: 'Light', color: 'bg-amber-500/20 text-amber-300', iconColor: '#fbbf24' }];
    }
    
    // --- Switch/Outlet (may have power meter combo) ---
    if (rawType === 'SWITCH') {
      labels.push({ Icon: ToggleOnIcon, label: 'Switch', color: 'bg-slate-500/20 text-slate-300', iconColor: '#94a3b8' });
      if (hasPowerMeterCapability) {
        labels.push({ Icon: ElectricBoltIcon, label: 'Meter', color: 'bg-green-500/20 text-green-300', iconColor: '#4ade80' });
      }
      return labels;
    }
    if (rawType === 'OUTLET' || rawType === 'PLUG') {
      labels.push({ Icon: PowerIcon, label: 'Outlet', color: 'bg-slate-500/20 text-slate-300', iconColor: '#94a3b8' });
      if (hasPowerMeterCapability) {
        labels.push({ Icon: ElectricBoltIcon, label: 'Meter', color: 'bg-green-500/20 text-green-300', iconColor: '#4ade80' });
      }
      return labels;
    }
    
    // --- Dedicated Power Meter ---
    if (rawType === 'POWER_METER') {
      return [{ Icon: ElectricBoltIcon, label: 'Meter', color: 'bg-green-500/20 text-green-300', iconColor: '#4ade80' }];
    }
    
    // --- Curtain (窗簾) ---
    if (rawType === 'CURTAIN') {
      return [{ Icon: BlindsIcon, label: 'Curtain', color: 'bg-teal-500/20 text-teal-300', iconColor: '#2dd4bf' }];
    }
    
    // --- Blind (捲簾) ---
    if (rawType === 'BLINDS' || rawType === 'BLIND' || rawType === 'ROLLER_SHADE') {
      return [{ Icon: BlindsIcon, label: 'Blind', color: 'bg-cyan-500/20 text-cyan-300', iconColor: '#22d3ee' }];
    }
    
    // --- Lock ---
    if (rawType === 'LOCK') {
      return [{ Icon: LockIcon, label: 'Security', color: 'bg-red-500/20 text-red-300', iconColor: '#f87171' }];
    }
    
    // --- Camera ---
    if (rawType === 'CAMERA') {
      return [{ Icon: VideocamIcon, label: 'Camera', color: 'bg-pink-500/20 text-pink-300', iconColor: '#f472b6' }];
    }
    
    // --- Speaker ---
    if (rawType === 'SPEAKER') {
      return [{ Icon: SpeakerIcon, label: 'Media', color: 'bg-violet-500/20 text-violet-300', iconColor: '#a78bfa' }];
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 2: SENSOR TYPES - Analyze capabilities for SENSOR/ULTRON_SENSOR/unknown
    // ═══════════════════════════════════════════════════════════════════════════
    // Only reach here if rawType is SENSOR, ULTRON_SENSOR, or empty/unknown
    
    // --- Button (Scene Button, etc.) ---
    if (hasButtonCapability) {
      return [{ Icon: TouchAppIcon, label: 'Button', color: 'bg-pink-500/20 text-pink-300', iconColor: '#f472b6' }];
    }
    
    // --- Door Sensor ---
    if (hasDoorCapability) {
      return [{ Icon: SensorsIcon, label: 'Door', color: 'bg-rose-500/20 text-rose-300', iconColor: '#fb7185' }];
    }
    
    // --- Motion + Lux combo ---
    if (hasMotionCapability && hasLuxCapability) {
      labels.push({ Icon: SensorsIcon, label: 'Motion', color: 'bg-violet-500/20 text-violet-300', iconColor: '#8b5cf6' });
      labels.push({ Icon: LightModeIcon, label: 'Lux', color: 'bg-amber-500/20 text-amber-300', iconColor: '#fbbf24' });
      return labels;
    }
    
    // --- Motion only ---
    if (hasMotionCapability) {
      return [{ Icon: SensorsIcon, label: 'Motion', color: 'bg-violet-500/20 text-violet-300', iconColor: '#8b5cf6' }];
    }
    
    // --- Presence + Lux combo ---
    if (hasOccupancyCapability && hasLuxCapability) {
      labels.push({ Icon: SensorsIcon, label: 'Presence', color: 'bg-purple-500/20 text-purple-300', iconColor: '#a78bfa' });
      labels.push({ Icon: LightModeIcon, label: 'Lux', color: 'bg-amber-500/20 text-amber-300', iconColor: '#fbbf24' });
      return labels;
    }
    
    // --- Presence only ---
    if (hasOccupancyCapability) {
      return [{ Icon: SensorsIcon, label: 'Presence', color: 'bg-purple-500/20 text-purple-300', iconColor: '#a78bfa' }];
    }
    
    // --- Lux only ---
    if (hasLuxCapability) {
      return [{ Icon: LightModeIcon, label: 'Lux', color: 'bg-amber-500/20 text-amber-300', iconColor: '#fbbf24' }];
    }
    
    // --- TH Sensor (Temperature & Humidity) ---
    if (hasTempHumidityCapability) {
      return [{ Icon: ThermostatIcon, label: 'TH Sensor', color: 'bg-orange-500/20 text-orange-300', iconColor: '#fb923c' }];
    }
    
    // --- Power Meter (capability-based for unknown types) ---
    if (hasPowerMeterCapability) {
      return [{ Icon: ElectricBoltIcon, label: 'Meter', color: 'bg-green-500/20 text-green-300', iconColor: '#4ade80' }];
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 3: TRAIT-BASED FALLBACK (for devices with unknown type but known traits)
    // ═══════════════════════════════════════════════════════════════════════════
    
    // --- Lighting (via traits) ---
    if (traits.includes('Brightness') || traits.includes('ColorSetting')) {
      return [{ Icon: LightbulbIcon, label: 'Light', color: 'bg-amber-500/20 text-amber-300', iconColor: '#fbbf24' }];
    }
    
    // --- HVAC (via traits) ---
    if (traits.includes('TemperatureSetting') && traits.includes('FanSpeed')) {
      return [{ Icon: AcUnitIcon, label: 'AC', color: 'bg-blue-500/20 text-blue-300', iconColor: '#60a5fa' }];
    }
    
    // --- Fan (via traits) - FanSpeed without TemperatureSetting = standalone fan ---
    // ✅ This handles TaiSEIA and other protocol endpoints that don't have explicit FAN type
    if (traits.includes('FanSpeed') && !traits.includes('TemperatureSetting')) {
      return [{ Icon: AirIcon, label: 'Fan', color: 'bg-cyan-500/20 text-cyan-300', iconColor: '#22d3d1' }];
    }
    
    // --- Curtain/Blinds (via traits) ---
    if (traits.includes('OpenClose')) {
      return [{ Icon: BlindsIcon, label: 'Curtain', color: 'bg-teal-500/20 text-teal-300', iconColor: '#2dd4bf' }];
    }
    
    // --- Lock (via traits) ---
    if (traits.includes('LockUnlock')) {
      return [{ Icon: LockIcon, label: 'Security', color: 'bg-red-500/20 text-red-300', iconColor: '#f87171' }];
    }
    
    // --- Speaker (via traits) ---
    if (traits.includes('Volume')) {
      return [{ Icon: SpeakerIcon, label: 'Media', color: 'bg-violet-500/20 text-violet-300', iconColor: '#a78bfa' }];
    }
    
    // --- Power Meter (via traits) ---
    if (traits.includes('EnergyStorage')) {
      return [{ Icon: ElectricBoltIcon, label: 'Meter', color: 'bg-green-500/20 text-green-300', iconColor: '#4ade80' }];
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 4: FALLBACK
    // ═══════════════════════════════════════════════════════════════════════════
    
    // Generic Sensor fallback for SENSOR/ULTRON_SENSOR
    if (rawType === 'ULTRON_SENSOR' || rawType === 'SENSOR') {
      return [{ Icon: RouterIcon, label: 'Sensor', color: 'bg-indigo-500/20 text-indigo-300', iconColor: '#818cf8' }];
    }
    
    // Unknown Device
    return [{ Icon: DevicesOtherIcon, label: rawType || 'Device', color: 'bg-slate-500/20 text-slate-300', iconColor: '#94a3b8' }];
  };

  // Helper: Get ALL device type labels for filtering (not just the primary one)
  // This ensures devices with multiple labels (e.g., "Presence" + "Lux") are found by either filter
  const getAllDeviceTypes = (device: Device): string[] => {
    const labels = getDeviceTypeLabels(device);
    return labels.map(l => l.label);
  };

  // Compute available device types for filter options
  // Collect ALL labels from ALL devices (not just primary)
  const availableDeviceTypes = React.useMemo(() => {
    const typeSet = new Set<string>();
    availableDevices.forEach(device => {
      const allTypes = getAllDeviceTypes(device);
      allTypes.forEach(type => typeSet.add(type));
    });
    // Sort alphabetically for consistent order
    const types = Array.from(typeSet).sort();
    logger.log(`📊 [FILTER] Available device types (${availableDevices.length} devices):`, types);
    return types;
  }, [availableDevices]);

  // Filter devices by search term AND device type
  const filteredAvailableDevices = availableDevices.filter(device => {
    // Type filter - check if ANY of the device's labels match the selected filter
    if (selectedTypeFilter !== 'all') {
      const allTypes = getAllDeviceTypes(device);
      if (!allTypes.includes(selectedTypeFilter)) return false;
    }
    
    // Search filter
    const nickname = (device.nickname || '').toLowerCase();
    const displayName = (device.displayName || '').toLowerCase();
    const deviceName = (device.name || device.deviceName || '').toLowerCase();
    // ✅ Fix: Use fallback pattern for sn (migration may have only deviceSN)
    const deviceSN = ((device as any).deviceSN || device.sn || '').toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    return nickname.includes(searchLower) ||
           displayName.includes(searchLower) ||
           deviceName.includes(searchLower) ||
           deviceSN.includes(searchLower);
  });

  // Draggable Device Component
  // ✅ Pass deviceZones and selectedForGroup as props to ensure memo comparison works correctly
  const DraggableDevice = React.memo(({ 
    device, 
    deviceZones, 
    selectedForGroup 
  }: { 
    device: Device;
    deviceZones: DeviceZone[];
    selectedForGroup: string[];
  }) => {
    // ✅ Use deviceSN field (from refactored Device interface) with backward compatibility
    // Type assertion needed because Device interface may not have deviceSN/deviceId yet
    const deviceSN = (device as any).deviceSN || device.sn || (device as any).deviceId || '';
    
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: deviceSN });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    // Find which groups this device belongs to
    const assignedGroups = deviceZones.filter(g => g.devices.includes(deviceSN));

    const isSelected = selectedForGroup.includes(deviceSN);

    return (
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center p-2 rounded-md hover:bg-slate-800/30 hover:bg-slate-700/50 transition-colors"
      >
        <div className="mr-2 cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
          <GripVertical className="w-4 h-4 text-slate-400" />
        </div>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => handleDeviceSelection(deviceSN, e.target.checked)}
          className="mr-2"
          onClick={(e) => e.stopPropagation()}
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white truncate">
            {device.nickname || device.displayName || device.name || device.deviceName || deviceSN}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-400">
              {deviceSN}
            </span>
            {/* Device type labels */}
            {getDeviceTypeLabels(device).map((typeInfo) => (
              <span 
                key={`${deviceSN}-${typeInfo.label}`} 
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full ${typeInfo.color}`}
              >
                <typeInfo.Icon sx={{ fontSize: 12, color: typeInfo.iconColor }} />
                {typeInfo.label}
              </span>
            ))}
            {/* Show group labels with unassign button */}
            {assignedGroups.map(group => (
              <span
                key={group.id}
                className="group/label relative px-2 py-0.5 text-[10px] bg-blue-900/30 text-blue-400 rounded-full flex items-center gap-1"
              >
                {group.name}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveDeviceFromGroup(group.id, deviceSN);
                  }}
                  className="opacity-0 group-hover/label:opacity-100 transition-opacity hover:text-red-500"
                  title={`Remove from ${group.name}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
        <button
          onClick={() => handleRemoveImportedDevice(deviceSN)}
          className="p-1 hover:bg-slate-700 rounded transition-colors"
        >
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>
    );
  }, (prevProps, nextProps) => {
    // ✅ Compare deviceSN (from refactored Device interface) with backward compatibility
    // Type assertion needed because Device interface may not have deviceSN/deviceId yet
    const prevDeviceSN = (prevProps.device as any).deviceSN || prevProps.device.sn || (prevProps.device as any).deviceId || '';
    const nextDeviceSN = (nextProps.device as any).deviceSN || nextProps.device.sn || (nextProps.device as any).deviceId || '';
    
    // ✅ Compare device data
    const deviceDataEqual = prevDeviceSN === nextDeviceSN &&
      JSON.stringify(prevProps.device) === JSON.stringify(nextProps.device);
    
    // ✅ Compare assigned groups (deviceZones may have changed)
    const prevAssignedGroups = prevProps.deviceZones.filter(g => g.devices.includes(prevDeviceSN));
    const nextAssignedGroups = nextProps.deviceZones.filter(g => g.devices.includes(nextDeviceSN));
    const assignedGroupsEqual = JSON.stringify(prevAssignedGroups) === JSON.stringify(nextAssignedGroups);
    
    // ✅ Compare selection status (selectedForGroup may have changed)
    const prevIsSelected = prevProps.selectedForGroup.includes(prevDeviceSN);
    const nextIsSelected = nextProps.selectedForGroup.includes(nextDeviceSN);
    const selectionEqual = prevIsSelected === nextIsSelected;
    
    // Only re-render if any of these changed
    return deviceDataEqual && assignedGroupsEqual && selectionEqual;
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className={`absolute top-0 h-screen bg-[#0C2340] border-r border-slate-700/30 transition-all duration-300 z-40 w-[960px] shadow-2xl ${
        appSidebarExpanded ? 'left-64' : 'left-16'
      } flex flex-col`}>
      {/* Header - Match AppSidebar Logo height */}
      <div className="h-[65px] flex items-center justify-between px-4 border-b border-slate-700/30">
        <h2 className="text-lg font-semibold text-white">Device Manager</h2>
      </div>

      {/* Content */}
      {true && (
        <>
          {/* Split View: Devices & Groups side by side */}
          <div className="flex-1 overflow-hidden flex">
            {/* Left: Import Devices Section */}
            <div className="w-[60%] border-r border-slate-700/30 p-3 flex flex-col overflow-hidden">
                {/* Import Section */}
                <div className="flex flex-col h-full space-y-3 min-h-0">
                  <div className="flex items-center justify-between flex-shrink-0">
                    <h3 className="text-sm font-medium text-slate-300">Import from your ID</h3>
                    <button
                      onClick={handleRefreshWithSync}
                      className="p-1 hover:bg-slate-700/30 hover:bg-slate-700/50 rounded transition-colors"
                      title="Sync & Refresh (syncs device states from cloud)"
                    >
                      <RefreshCw className={`w-4 h-4 text-slate-400 text-slate-300 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>

                  {/* Group Selector */}
                  <select
                    value={selectedSourceGroup}
                    onChange={(e) => {
                      setSelectedSourceGroup(e.target.value);
                      setSelectedTypeFilter('all');  // Reset type filter when changing source
                    }}
                    className="w-full px-3 py-2 border border-slate-700/50 rounded-lg bg-slate-800/50 text-white text-sm flex-shrink-0"
                  >
                    <option value="">Select Location</option>
                    {availableGroups.map(group => {
                      const groupId = group.id || group.groupId;
                      return (
                        <option key={groupId} value={groupId}>
                          {group.name || groupId}
                        </option>
                      );
                    })}
                  </select>

                  {/* Search */}
                  <div className="relative flex-shrink-0">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search devices..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-slate-700/50 rounded-lg bg-slate-800/50 text-white text-sm"
                    />
                  </div>

                  {/* Device Type Filter Chips */}
                  {availableDeviceTypes.length > 1 && (
                    <div className="flex flex-wrap gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => setSelectedTypeFilter('all')}
                        className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                          selectedTypeFilter === 'all'
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-800/50 text-slate-400 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        All
                      </button>
                      {availableDeviceTypes.map(type => (
                        <button
                          key={type}
                          onClick={() => setSelectedTypeFilter(type)}
                          className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                            selectedTypeFilter === type
                              ? 'bg-blue-500 text-white'
                              : 'bg-slate-800/50 text-slate-400 text-slate-300 hover:bg-slate-700'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Available Devices */}
                  <div className="space-y-2 flex-1 flex flex-col min-h-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">
                        Available Devices ({filteredAvailableDevices.length})
                      </span>
                      {selectedForGroup.length > 0 && (
                        <span className="text-xs text-blue-400 font-medium">
                          {selectedForGroup.length} selected
                        </span>
                      )}
                    </div>
                    {/* Helper text */}
                    {selectedForGroup.length === 0 && selectedGroups.length === 0 && (
                      <div className="text-xs text-slate-400 italic px-2">
                        💡 Select devices here, then check zones to import
                      </div>
                    )}
                    {selectedForGroup.length > 0 && selectedGroups.length === 0 && (
                      <div className="text-xs text-blue-400 px-2">
                        ✓ Now check zones on the right →
                      </div>
                    )}
                    {selectedForGroup.length > 0 && selectedGroups.length > 0 && (
                      <div className="text-xs text-green-600 text-green-400 font-medium px-2">
                        ✓ Ready! Click Import button →
                      </div>
                    )}

                    {/* Select All Checkbox */}
                    {filteredAvailableDevices.filter(d => !d.imported).length > 0 && (
                      <label className="flex items-center px-2 py-1 hover:bg-slate-800/30 hover:bg-slate-700/50 rounded-md cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filteredAvailableDevices.filter(d => !d.imported).every(d => selectedForGroup.includes(d.sn))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const allNotImported = filteredAvailableDevices.filter(d => !d.imported).map(d => d.sn);
                              setSelectedForGroup(Array.from(new Set([...selectedForGroup, ...allNotImported])));
                            } else {
                              const notImportedSNs = filteredAvailableDevices.filter(d => !d.imported).map(d => d.sn);
                              setSelectedForGroup(selectedForGroup.filter(sn => !notImportedSNs.includes(sn)));
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm font-medium text-slate-300">
                          Select All ({filteredAvailableDevices.filter(d => !d.imported).length})
                        </span>
                      </label>
                    )}

                    <div className="flex-1 overflow-y-auto space-y-1 border border-slate-500/50 rounded-lg p-2 min-h-0">
                      {loading ? (
                        <div className="text-center py-4 text-slate-400 text-sm">
                          Loading devices...
                        </div>
                      ) : filteredAvailableDevices.length === 0 ? (
                        <div className="text-center py-4 text-slate-400 text-sm">
                          No devices found
                        </div>
                      ) : (
                        filteredAvailableDevices.map(device => (
                          <React.Fragment key={device.sn}>
                          <label
                            className={`flex items-center p-2 rounded-md hover:bg-slate-800/30 hover:bg-slate-700/50 cursor-pointer transition-colors overflow-hidden ${
                              device.imported ? 'opacity-50' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedForGroup.includes(device.sn)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedForGroup([...selectedForGroup, device.sn]);
                                } else {
                                  setSelectedForGroup(selectedForGroup.filter(d => d !== device.sn));
                                }
                              }}
                              // ✅ Allow selecting imported devices to add them to additional groups
                              // Devices can belong to multiple virtual groups
                              className="mr-2 flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-white truncate">
                                {device.deviceName || device.displayName || device.sn}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-slate-400">
                                  {device.sn}
                                </span>
                                {getDeviceTypeLabels(device).map((typeInfo) => (
                                  <span 
                                    key={`${device.sn}-${typeInfo.label}`} 
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full ${typeInfo.color}`}
                                  >
                                    <typeInfo.Icon sx={{ fontSize: 12, color: typeInfo.iconColor }} />
                                    {typeInfo.label}
                                  </span>
                                ))}
                                {/* Multi-channel badge and expand button */}
                                {(device as any).channels && (device as any).channels.length > 1 && (() => {
                                  const baseSN = (device as any).baseSN || device.sn.split('#')[0];
                                  const isChannelsExpanded = expandedChannels.has(baseSN);
                                  return (
                                    <>
                                      {/* Expand/Collapse channels button */}
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setExpandedChannels(prev => {
                                            const newSet = new Set(prev);
                                            if (newSet.has(baseSN)) {
                                              newSet.delete(baseSN);
                                            } else {
                                              newSet.add(baseSN);
                                            }
                                            return newSet;
                                          });
                                        }}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 transition-colors"
                                        title={`${(device as any).channels.length} channels - Click to ${isChannelsExpanded ? 'collapse' : 'show'} channels`}
                                      >
                                        <ChevronRight className={`w-3 h-3 transition-transform ${isChannelsExpanded ? 'rotate-90' : ''}`} />
                                        {(device as any).channels.length}ch
                                      </button>
                                      {/* Display mode toggle (merged vs expanded) */}
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleToggleMultiChannelDisplayMode(baseSN);
                                        }}
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-slate-600/30 text-slate-400 hover:bg-slate-500/30 transition-colors"
                                        title={multiChannelDisplayModes[baseSN] === 'expanded' ? 'Switch to merged mode' : 'Switch to expanded mode (separate cards)'}
                                      >
                                        {multiChannelDisplayModes[baseSN] === 'expanded' ? (
                                          <Layers className="w-3 h-3" />
                                        ) : (
                                          <LayoutGrid className="w-3 h-3" />
                                        )}
                                      </button>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                            {device.imported && (() => {
                              // Find which groups this device belongs to
                              const groupsForDevice = deviceZones.filter(group => group.devices.includes(device.sn));
                              return (
                                <div className="flex items-center gap-1 ml-1 flex-shrink-0 max-w-[120px] overflow-hidden">
                                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                  {groupsForDevice.length > 0 && (
                                    <div className="flex items-center gap-1 flex-wrap overflow-hidden">
                                      {groupsForDevice.slice(0, 2).map(group => (
                                        <span 
                                          key={group.id}
                                          className="px-1.5 py-0.5 text-[10px] bg-green-900/30 text-green-400 rounded whitespace-nowrap truncate max-w-[50px]"
                                          title={group.name}
                                        >
                                          {group.name}
                                        </span>
                                      ))}
                                      {groupsForDevice.length > 2 && (
                                        <span className="text-[10px] text-green-600 text-green-400">
                                          +{groupsForDevice.length - 2}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {/* ✅ Online status icon - always at the rightmost position */}
                            <span className={`ml-auto w-2 h-2 rounded-full flex-shrink-0 ${
                              device.status === 'online' ? 'bg-green-500' : 'bg-red-500'
                            }`} />
                          </label>
                          {/* Expanded channels list for multi-channel devices in merged mode */}
                          {(device as any).channels && (device as any).channels.length > 1 && (() => {
                            const baseSN = (device as any).baseSN || device.sn.split('#')[0];
                            const isChannelsExpanded = expandedChannels.has(baseSN);
                            if (!isChannelsExpanded) return null;
                            
                            return (
                              <div className="ml-6 mb-2 border-l-2 border-purple-500/30 pl-3 space-y-1">
                                {(device as any).channels.map((channel: any, index: number) => (
                                  <div
                                    key={channel.sn || `${baseSN}-ch-${index}`}
                                    className="flex items-center gap-2 py-1 px-2 rounded bg-slate-800/30 text-xs"
                                  >
                                    <span className="text-purple-400 font-mono">
                                      #{channel.endpoint || index + 1}
                                    </span>
                                    <span className="text-slate-300 truncate flex-1">
                                      {channel.nickname || channel.displayName || `Channel ${index + 1}`}
                                    </span>
                                    {channel.type && (
                                      <span className="text-[10px] text-slate-500 bg-slate-700/50 px-1.5 py-0.5 rounded">
                                        {channel.type}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </React.Fragment>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>

            {/* Right: Groups Section */}
            <div className="w-[40%] p-3 flex flex-col overflow-hidden">
                {/* Groups Section */}
                <div className="space-y-3 flex flex-col h-full overflow-hidden">
                  <div className="flex items-center justify-between flex-shrink-0">
                    <h3 className="text-sm font-medium text-slate-300">Zone Management</h3>
                    <div className="flex items-center gap-2">
                      {/* Health Check button */}
                      <button
                        onClick={() => handleHealthCheck(true)}
                        disabled={isCheckingHealth}
                        className="p-1 hover:bg-slate-700/30 hover:bg-slate-700/50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Verify Subscriptions (Auto-heal enabled)"
                      >
                        <ShieldCheck className={`w-4 h-4 ${
                          healthCheckResult
                            ? healthCheckResult.errors > 0
                              ? 'text-red-500'
                              : healthCheckResult.warnings > 0
                              ? 'text-yellow-500'
                              : 'text-green-500'
                            : 'text-slate-400 text-slate-300'
                        } ${isCheckingHealth ? 'animate-pulse' : ''}`} />
                      </button>
                      {/* Import button - show when both devices and groups are selected */}
                      {selectedForGroup.length > 0 && selectedGroups.length > 0 && (
                        <button
                          onClick={handleImportToGroups}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md transition-colors flex items-center gap-1"
                          title={`Import ${selectedForGroup.length} device(s) to ${selectedGroups.length} group(s)`}
                        >
                          <Download className="w-3.5 h-3.5" />
                          <span>Import</span>
                        </button>
                      )}
                      <button
                        onClick={() => setShowCreateGroup(true)}
                        className="p-1 hover:bg-slate-700/30 hover:bg-slate-700/50 rounded transition-colors"
                        title="Create Zone"
                      >
                        <FolderPlus className="w-4 h-4 text-slate-400 text-slate-300" />
                      </button>
                    </div>
                  </div>

                  {/* Create Group Form */}
                  {showCreateGroup && (
                    <div className="p-3 bg-slate-800/30 bg-slate-800/50 rounded-lg space-y-2">
                      <input
                        type="text"
                        placeholder="Zone name..."
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-700/50 rounded-md bg-[#0C2340] text-white text-sm"
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={handleCreateGroup}
                          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Create
                        </button>
                        <button
                          onClick={() => {
                            setShowCreateGroup(false);
                            setNewGroupName('');
                          }}
                          className="px-4 py-2 bg-slate-700 text-slate-300 text-white text-sm rounded-md hover:bg-slate-500 hover:bg-slate-700 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Groups List */}
                  <div className="space-y-2 flex-1 overflow-y-auto">
                    {deviceZones.length === 0 ? (
                      <div className="text-center py-8 text-slate-400 text-sm">
                        <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        No groups created yet
                      </div>
                    ) : (
                      deviceZones.map(group => (
                        <DroppableGroupComponent
                          key={group.id}
                          group={group}
                          isExpanded={expandedGroups.has(group.id)}
                          isRenaming={renamingGroupId === group.id}
                          tempGroupName={tempGroupName}
                          isSelected={selectedGroups.includes(group.id)}
                          deviceShadowMap={deviceShadowMap}
                          onToggleExpanded={() => {
                            setExpandedGroups(prev => {
                              const newSet = new Set(prev);
                              if (newSet.has(group.id)) {
                                newSet.delete(group.id);
                              } else {
                                newSet.add(group.id);
                              }
                              return newSet;
                            });
                          }}
                          onSelect={() => handleGroupSelection(group.id)}
                          onStartRename={() => handleStartRename(group.id, group.name)}
                          onRename={(newName) => handleRenameGroup(group.id, newName)}
                          onCancelRename={handleCancelRename}
                          onTempNameChange={setTempGroupName}
                          onDelete={() => handleRemoveGroup(group.id)}
                          onRemoveDevice={(deviceSN) => handleRemoveDeviceFromGroup(group.id, deviceSN)}
                          onReorderDevices={handleReorderDevices}
                          controlGroups={deviceControlGroups.filter(cg => cg.zoneId === group.id)}
                          onUngroupControlGroup={handleUngroupControlGroup}
                        />
                      ))
                    )}
                  </div>

                </div>
              </div>
          </div>
        </>
      )}
    </div>

    {/* Drag Overlay */}
    <DragOverlay dropAnimation={null}>
      {activeDevice ? (
        <div className="bg-[#0C2340] p-2 rounded-lg shadow-xl border-2 border-blue-500">
          <div className="text-sm font-medium text-white">
            {/* ✅ Use deviceSN field (from refactored Device interface) with backward compatibility */}
            {/* Type assertion needed because Device interface may not have deviceSN/deviceId yet */}
            {activeDevice.nickname || activeDevice.displayName || activeDevice.name || activeDevice.deviceName || ((activeDevice as any).deviceSN || activeDevice.sn || (activeDevice as any).deviceId)}
          </div>
          <div className="text-xs text-slate-400">
            {(activeDevice as any).deviceSN || activeDevice.sn || (activeDevice as any).deviceId}
          </div>
        </div>
      ) : null}
    </DragOverlay>

    {/* Sync Notification */}
    {syncNotification && (
      <div className="fixed bottom-4 right-4 z-50 max-w-md">
        <div className={`p-4 rounded-lg shadow-lg border-2 ${
          syncNotification.type === 'success' ? 'bg-green-50 bg-green-900/20 border-green-500 border-green-700' :
          syncNotification.type === 'error' ? 'bg-red-900/20 border-red-500 border-red-700' :
          'bg-blue-900/20 border-blue-500 border-blue-700'
        }`}>
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                syncNotification.type === 'success' ? 'text-green-100' :
                syncNotification.type === 'error' ? 'text-red-900 text-red-100' :
                'text-blue-100'
              }`}>
                {syncNotification.message}
              </p>
            </div>
            <button
              onClick={() => setSyncNotification(null)}
              className={`text-slate-400 hover:text-slate-400 hover:text-slate-200 transition-colors`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    )}
  </DndContext>
  );
}